import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { itemsService, cartsService, savedItemsService, productService, searchService, budgetOptimizationService, exchangeRatesService } from '../services/api'
import { useTheme } from '../contexts/ThemeContext'
import ToolsMenu from '../components/ToolsMenu'
import CompareCartPicker from '../components/CompareCartPicker'
import LibraryPanel from '../components/LibraryPanel'
import SelectionDock from '../components/SelectionDock'
import AppDock from '../components/AppDock'
import LanguageSelector from '../components/LanguageSelector'
import ThemeSwitcher from '../components/ThemeSwitcher'
import NotificationBell from '../components/NotificationBell'
import ConfirmModal from '../components/ConfirmModal'
import PriceHistoryModal from '../components/PriceHistoryModal'
import BudgetOptimizationModal from '../components/BudgetOptimizationModal'
import ShareCartModal from '../components/ShareCartModal'
import { useNotifications } from '../contexts/NotificationContext'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../hooks/useTranslation'
import { useCartsQuery } from '../hooks/useCartsQuery'
import { useSavedItemsQuery } from '../hooks/useSavedItemsQuery'
import { sanitizeUrl } from '../utils/sanitize'
import { displayItemLink, formatPlaceCardLabel } from '../utils/itemPresentation'
import { getUserId } from '../utils/sessionUser'
import { apiUrl } from '../utils/apiBase'
import { describeItemsState, itemsRefetchInterval, shouldRetryItems } from '../utils/itemQueryPolicy'
import PegTopLoader from '../components/PegTopLoader'
import { useSignedMediaQueryRenewal } from '../hooks/useSignedMediaQueryRenewal'
import ProductImage from '../components/ProductImage'
import { savedItemsQueryKey } from '../utils/libraryIsolation'
import {
  applyCartFilters,
  countActiveFilters,
  emptyCartFilters,
  forgetLegacyFilterPrefs,
  sanitizeCartFilters,
} from '../utils/cartFiltersPolicy'
import {
  canStartItemRefresh,
  describePriceRefreshError,
  formatLastPriceCheck,
  isItemRefreshing,
  isValidScrapedPrice,
  itemsPriceQueryKey,
  nextPriceRefreshUiState,
  nextRefreshingIds,
  shouldHandleExtensionSync,
} from '../utils/priceRefreshPolicy'
import JSZip from 'jszip'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import RefreshMethodContextMenu from '../components/RefreshMethodContextMenu'
import LiquidGlassProgressBar from '../components/LiquidGlassProgressBar'
import AddProductModal from '../components/AddProductModal'
import AddProductSuccess from '../components/AddProductSuccess'
import AddProductChooser from '../components/scan/AddProductChooser'
import ScanProductFlow from '../components/scan/ScanProductFlow'
import ProductPlaceSheet from '../components/scan/ProductPlaceSheet'
import { createScanOutboxSynchronizer, scanOutbox } from '../services/scan/scanOutbox'
import { resolveJpyEurRate } from '../services/scan/exchangeRateCache'
import {
  applyConversionToPayload,
  classifyScanError,
  conversionIsPending,
  describeScanSaveError,
  isConfirmedScanSave,
} from '../services/scan/scanSyncPolicy'
import EditProductModal from '../components/EditProductModal'
import RollingPrice from '../components/RollingPrice'
import { attachPendingScans, buildAddSuccessPayload, mergeCreatedItem } from '../utils/addProductOutcome'
import { createClientMutationId } from '../utils/clientMutationId'
import { mergeUpdatedItem, nextEditProductUiState } from '../utils/editProductOutcome'
import { budgetNoticeText } from '../utils/notificationPolicy'
import { budgetSummaryModel } from '../utils/budgetSummary'
import ProductImageLightbox from '../components/ProductImageLightbox'
import { applyStoreToMeta, persistImageUrl, readItemGallery, readItemStore, resolveCardImage } from '../utils/itemMeta'
import { IconPin } from '../components/scan/ScanIcons'
import { formatJpy, observedJpyTotal, readObservedPricing } from '../utils/japanPrice'
import {
  ITEM_LONG_PRESS_MS,
  isItemLongPressTarget,
  pointerMovedBeyondThreshold,
} from '../utils/itemSelectionLongPress'
import { shouldEnableSemanticQuery } from '../utils/semanticSearchPolicy'
import GridItemActionsMenu from '../components/GridItemActionsMenu'
import KebabGlyph from '../components/KebabGlyph'
import SemanticSearchPanel from '../components/SemanticSearchPanel'
import CartFiltersPanel from '../components/CartFiltersPanel'
import LiquidGlassButton from '../components/LiquidGlassButton'
import { buildExportedCartHtml } from '../utils/exportCartHtml'
import { useLongPressMenu } from '../hooks/useLongPressMenu'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { PHONE_LAYOUT_QUERY } from '../utils/notificationSwipe'
import '../pages/CartDetailPage.css'

function HeaderCommandBar({ children }) {
  const scrollerRef = useRef(null)
  const [overflow, setOverflow] = useState({ start: false, end: false })

  useEffect(() => {
    const node = scrollerRef.current
    if (!node) return undefined
    const update = () => {
      const max = node.scrollWidth - node.clientWidth
      setOverflow({
        start: node.scrollLeft > 2,
        end: max > 2 && node.scrollLeft < max - 2,
      })
    }
    update()
    node.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => {
      node.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [])

  return (
    <div
      className={`header-cmd-bar${overflow.start ? ' is-overflow-start' : ''}${overflow.end ? ' is-overflow-end' : ''}`}
    >
      <div className="header-cmd-scroll" ref={scrollerRef}>
        {children}
      </div>
    </div>
  )
}

function BudgetCancelGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function BudgetSaveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M5 12l5 5 9-10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ReloadSyncGlyph() {
  return (
    <svg className="scan-item-sync-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.6-6.3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M21 4v5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CartDetailPage() {
  const { cartId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { theme } = useTheme()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { addNotification, removeNotification } = useNotifications()
  const isPhoneLayout = useMediaQuery(PHONE_LAYOUT_QUERY)
  const { user, token } = useAuth()
  const currentUserId = getUserId(user)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [semanticSearchTerm, setSemanticSearchTerm] = useState('')
  const [debouncedSemanticSearchTerm, setDebouncedSemanticSearchTerm] = useState('')
  const [showSemanticDropdown, setShowSemanticDropdown] = useState(false)
  const searchInputRef = useRef(null)
  const [highlightedItemId, setHighlightedItemId] = useState(null)
  const gridMenuAnchorRef = useRef(null)
  const gridMenuItemRef = useRef(null)
  const cardsAnchorRef = useRef(null)
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [libraryPlacement, setLibraryPlacement] = useState({ mode: 'beside', workspacePadRight: 0 })
  const handleLibraryPlacement = useCallback((next) => {
    setLibraryPlacement(next || { mode: 'beside', workspacePadRight: 0 })
  }, [])
  const imageErrorItemsRef = useRef(new Set())
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAddChooser, setShowAddChooser] = useState(false)
  const [showScanFlow, setShowScanFlow] = useState(false)
  const [placeItem, setPlaceItem] = useState(null)

  useEffect(() => {
    if (isPhoneLayout) return undefined
    setShowAddChooser(false)
    setShowScanFlow(false)
    setPlaceItem(null)
    return undefined
  }, [isPhoneLayout])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [showLibraryModal, setShowLibraryModal] = useState(false)
  const [showComparePicker, setShowComparePicker] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [showToolsMenu, setShowToolsMenu] = useState(false)
  const [showBudgetModal, setShowBudgetModal] = useState(false)
  const [budgetValue, setBudgetValue] = useState('')
  const [showOptimizationModal, setShowOptimizationModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
  const [itemToDelete, setItemToDelete] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  const itemToDeleteRef = useRef(null)
  itemToDeleteRef.current = itemToDelete
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false)
  const [selectedItemForHistory, setSelectedItemForHistory] = useState(null)
  const [openGridMenuId, setOpenGridMenuId] = useState(null) // Menu déroulant pour la vue grille
  const [gridMenuArrow, setGridMenuArrow] = useState('down')
  const addProductButtonRef = useRef(null)
  const toolsButtonRef = useRef(null)
  const refreshAllButtonRef = useRef(null)
  const editTriggerRef = useRef(null)
  const historyTriggerRef = useRef(null)
  const editSnapshotRef = useRef(null)
  const [justAddedIds, setJustAddedIds] = useState(() => new Set())
  const [addProductError, setAddProductError] = useState('')
  const [addSuccessPayload, setAddSuccessPayload] = useState(null)
  const [editProductError, setEditProductError] = useState('')
  const [priceRoll, setPriceRoll] = useState(null)
  
  // États pour les filtres avancés
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [cartFilters, setCartFilters] = useState(() => {
    forgetLegacyFilterPrefs()
    return emptyCartFilters()
  })
  
  // État pour le menu contextuel de méthode d'actualisation
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    target: null // 'single' ou 'all'
  })
  // Récupérer la méthode par défaut depuis localStorage et mapper les valeurs
  const getDefaultRefreshMethod = () => {
    const saved = localStorage.getItem('defaultPriceUpdateMethod')
    if (!saved) return 'auto'
    // Mapper les valeurs : 'automatique' -> 'auto', 'extension' -> 'extension', 'client' -> 'client', 'serveur' -> 'server'
    const mapping = {
      'automatique': 'auto',
      'extension': 'extension',
      'client': 'client',
      'serveur': 'server'
    }
    return mapping[saved] || 'auto'
  }
  const [refreshMethod, setRefreshMethod] = useState(getDefaultRefreshMethod) // 'auto', 'extension', 'client', 'server'
  const [skipWhitelist, setSkipWhitelist] = useState(false) // Si True, ignore la whitelist
  
  // Écouter les changements de la méthode par défaut dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'defaultPriceUpdateMethod') {
        const mapping = {
          'automatique': 'auto',
          'extension': 'extension',
          'client': 'client',
          'serveur': 'server'
        }
        const newMethod = mapping[e.newValue] || 'auto'
        setRefreshMethod(newMethod)
      }
    }
    
    // Écouter les changements de storage (depuis d'autres onglets)
    window.addEventListener('storage', handleStorageChange)
    
    // Écouter les changements depuis le même onglet via un événement personnalisé
    const handleCustomStorageChange = () => {
      const saved = localStorage.getItem('defaultPriceUpdateMethod')
      if (saved) {
        const mapping = {
          'automatique': 'auto',
          'extension': 'extension',
          'client': 'client',
          'serveur': 'server'
        }
        const newMethod = mapping[saved] || 'auto'
        setRefreshMethod(newMethod)
      }
    }
    
    window.addEventListener('defaultPriceUpdateMethodChanged', handleCustomStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('defaultPriceUpdateMethodChanged', handleCustomStorageChange)
    }
  }, [])
  const [refreshProgress, setRefreshProgress] = useState({ current: 0, total: 0, isActive: false, isExiting: false })
  const refreshingIdsRef = useRef(new Set())
  const lastExtensionSyncRef = useRef('')
  const [refreshingItemIds, setRefreshingItemIds] = useState(() => new Set())

  const beginItemRefresh = useCallback((itemId) => {
    if (!canStartItemRefresh(refreshingIdsRef.current, itemId)) {
      return false
    }
    refreshingIdsRef.current = nextRefreshingIds(refreshingIdsRef.current, itemId, 'add')
    setRefreshingItemIds(new Set(refreshingIdsRef.current))
    return true
  }, [])

  const endItemRefresh = useCallback((itemId) => {
    refreshingIdsRef.current = nextRefreshingIds(refreshingIdsRef.current, itemId, 'remove')
    setRefreshingItemIds(new Set(refreshingIdsRef.current))
  }, [])

  const itemsCacheKey = itemsPriceQueryKey(cartId, currentUserId)
  const cartIdRef = useRef(cartId)
  const itemsCacheKeyRef = useRef(itemsCacheKey)
  cartIdRef.current = cartId
  itemsCacheKeyRef.current = itemsCacheKey
  const savedKey = savedItemsQueryKey(currentUserId)
  const advancedFiltersButtonRef = useRef(null)
  const [viewMode, setViewMode] = useState('list') // 'list' ou 'grid'
  
  useEffect(() => {
    setSearchTerm('')
    setCartFilters(emptyCartFilters())
  }, [cartId])

  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries(['items', cartId])
    }, 5 * 60 * 1000) // renouveler régulièrement les URLs signées
    return () => clearInterval(interval)
  }, [cartId, queryClient])

  // Fonction pour ouvrir le modal de budget (création ou édition)
  const handleOpenBudgetModal = () => {
    // Si un budget existe, pré-remplir avec la valeur actuelle
    if (currentCart?.budget && currentCart.budget > 0) {
      setBudgetValue(currentCart.budget.toString())
    } else {
      setBudgetValue('')
    }
    setShowBudgetModal(true)
  }

  const openRefreshAllMethodMenu = useCallback((event) => {
    const rect = event?.currentTarget?.getBoundingClientRect?.()
      || refreshAllButtonRef.current?.getBoundingClientRect?.()
    setContextMenu({
      isOpen: true,
      position: {
        x: event?.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
        y: event?.clientY ?? (rect ? rect.bottom + 8 : 0),
      },
      target: 'all',
    })
  }, [])
  const refreshAllMenu = useLongPressMenu(openRefreshAllMethodMenu)
  const [selectedImage, setSelectedImage] = useState(null)
  const [cartPriceSort, setCartPriceSort] = useState(null) // null, 'asc', 'desc'
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState(new Set())
  // Utiliser localStorage pour persister les items copiés entre les navigations de paniers
  const [copiedItems, setCopiedItems] = useState(() => {
    try {
      const saved = localStorage.getItem('copiedItems')
      return saved ? JSON.parse(saved) : []
    } catch (e) {
      return []
    }
  })
  
  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    try {
      if (copiedItems.length > 0) {
        localStorage.setItem('copiedItems', JSON.stringify(copiedItems))
      } else {
        localStorage.removeItem('copiedItems')
      }
    } catch (e) {
      console.error('Erreur sauvegarde copiedItems:', e)
    }
  }, [copiedItems])
  const longPressRef = useRef({
    timer: 0,
    itemId: null,
    x: 0,
    y: 0,
    cancelled: false,
    activated: false,
    pointerId: null,
  })
  const longPressActivatedRef = useRef(false)
  const scanSyncRef = useRef(null)
  const selectionModeActiveRef = useRef(false)
  const recentlySelectedByLongPressRef = useRef(new Set())
  const clearAddSuccess = useCallback(() => setAddSuccessPayload(null), [])
  const markItemJustAdded = useCallback((itemId) => {
    if (!itemId) return
    setJustAddedIds((prev) => new Set(prev).add(itemId))
    window.setTimeout(() => {
      setJustAddedIds((prev) => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }, 520)
  }, [])

  const {
    data: itemsData,
    isLoading,
    isPending,
    isFetching,
    isSuccess: itemsSuccess,
    isPlaceholderData: itemsPlaceholder,
    error: itemsError,
    refetch: refetchItems,
  } = useQuery({
    queryKey: itemsCacheKey,
    queryFn: async ({ queryKey, signal }) => {
      const requestedCartId = queryKey[1]
      const server = await itemsService.list(requestedCartId, { signal })
      if (!Array.isArray(server)) {
        throw new Error('Réponse articles invalide')
      }
      const entries = await scanOutbox.list().catch(() => [])
      return attachPendingScans(server, entries, requestedCartId)
    },
    enabled: !!cartId && !!currentUserId && !!token,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 10_000,
    retry: shouldRetryItems,
    placeholderData: (previousData, previousQuery) => {
      if (!Array.isArray(previousData)) return undefined
      if (String(previousQuery?.queryKey?.[1] || '') !== String(cartId || '')) return undefined
      return previousData
    },
    // Rafraîchir la liste toutes les 10 s quand la page est visible (fallback si message extension non reçu)
    refetchInterval: itemsRefetchInterval,
    refetchIntervalInBackground: false,
  })
  const itemsState = describeItemsState({
    isLoading,
    isPending,
    isFetching,
    isSuccess: itemsSuccess,
    isPlaceholderData: itemsPlaceholder,
    data: itemsData,
    error: itemsError,
  })
  const items = Array.isArray(itemsData) ? itemsData : []
  useSignedMediaQueryRenewal(itemsCacheKey, !!cartId && !!currentUserId && !!token)
  const itemsLoaded = itemsState === 'success'
    || itemsState === 'refreshing'
    || itemsState === 'refresh-error'
    || itemsState === 'empty'
  const itemsStatus = itemsError?.response?.status
  const itemsErrorMessage =
    itemsError?.response?.data?.detail ||
    itemsError?.message ||
    'Impossible de récupérer les articles de ce panier.'

  const { data: cartsData, refetch: refetchCarts } = useCartsQuery()
  const carts = cartsData ?? []

  const currentCart = carts.find(cart => cart.id === cartId)
  const cartUnavailable = Array.isArray(cartsData) && Boolean(cartId) && !currentCart

  const fromAnalytics = Boolean(location.state?.fromAnalytics)
  const goBackFromCart = () => {
    navigate('/carts', { state: { activeApp: fromAnalytics ? 'analytics' : 'carts' } })
  }
  
  // Debug: vérifier que le budget est bien présent
  useEffect(() => {
    if (currentCart) {
      console.log('Current cart:', currentCart)
      console.log('Budget:', currentCart.budget, 'Type:', typeof currentCart.budget)
      console.log('Budget condition:', currentCart.budget != null && currentCart.budget > 0)
      console.log('Tous les paniers:', carts.map(c => ({ id: c.id, name: c.name, budget: c.budget })))
    }
  }, [currentCart, carts])

  const {
    data: savedItemsData,
    isLoading: isLoadingLibrary,
    isError: isLibraryError,
    error: libraryError,
  } = useSavedItemsQuery()
  const savedItems = Array.isArray(savedItemsData) ? savedItemsData : []

  useEffect(() => {
    const handleExtensionMessage = (event) => {
      if (event.data?.source !== 'extension') return
      const activeCartId = cartIdRef.current
      const cacheKey = itemsCacheKeyRef.current

      if (event.data.type === 'ITEM_ADDED_TO_CART') {
        const { cartId: updatedCartId } = event.data.data || {}
        if (String(updatedCartId) === String(activeCartId)) {
          queryClient.invalidateQueries({ queryKey: cacheKey })
        }
        queryClient.invalidateQueries({ queryKey: ['carts'] })
      }

      if (event.data.type === 'ITEM_PRICE_UPDATED') {
        if (!shouldHandleExtensionSync(lastExtensionSyncRef, event.data)) {
          return
        }
        const { cartId: updatedCartId, itemId } = event.data.data || {}
        if (String(updatedCartId) === String(activeCartId)) {
          queryClient.invalidateQueries({ queryKey: cacheKey })
          if (itemId) {
            queryClient.invalidateQueries({ queryKey: ['priceHistory', itemId] })
          }
        }
        queryClient.invalidateQueries({ queryKey: ['carts'] })
      }
    }

    window.addEventListener('message', handleExtensionMessage)
    return () => window.removeEventListener('message', handleExtensionMessage)
  }, [queryClient])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSemanticSearchTerm(semanticSearchTerm)
    }, 320)

    return () => clearTimeout(timer)
  }, [semanticSearchTerm])

  const semanticQuery = (debouncedSemanticSearchTerm || '').trim()
  const {
    data: semanticResults,
    isLoading: isLoadingSemantic,
    isFetching: isFetchingSemantic,
    isError: isSemanticError,
    error: semanticError,
  } = useQuery({
    queryKey: ['semantic-search-dropdown', semanticQuery],
    queryFn: ({ signal }) => searchService.semantic(semanticQuery, 0.35, 20, signal),
    enabled: shouldEnableSemanticQuery(semanticQuery, showSemanticDropdown),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => error?.response?.status !== 503 && failureCount < 1,
  })

  const semanticItems = semanticResults?.results?.items || []
  const semanticSavedItems = semanticResults?.results?.saved_items || []

  const createMutation = useMutation({
    mutationFn: (item) => itemsService.create(cartId, item),
    onSuccess: async (created, submitted) => {
      const mutationId = submitted?.client_mutation_id
      if (created?.id && !String(created.id).startsWith('pending-')) {
        queryClient.setQueryData(itemsCacheKey, (prev) => mergeCreatedItem(prev, {
          ...created,
          client_mutation_id: mutationId || created.client_mutation_id,
        }))
        markItemJustAdded(created.id)
        if (mutationId) await scanOutbox.remove(mutationId).catch(() => {})
      }
      const pendingFx = conversionIsPending(created) || conversionIsPending(submitted)
      const notice = pendingFx ? 'Produit enregistré — conversion en attente' : ''
      const payload = buildAddSuccessPayload(created, notice)
      if (payload) setAddSuccessPayload(payload)
      setShowAddModal(false)
      setShowScanFlow(false)
      setShowAddChooser(false)
      setAddProductError('')
      queryClient.invalidateQueries({ queryKey: ['semantic-search-dropdown'] })
      try {
        const server = await itemsService.list(cartId)
        const entries = await scanOutbox.list().catch(() => [])
        queryClient.setQueryData(itemsCacheKey, attachPendingScans(server, entries, cartId))
      } catch {
        // La fusion locale reste affichée si la relecture échoue.
      }
      if (submitted?.item_meta?.identification_source || submitted?.item_meta?.barcode) {
        void productService.confirmIdentification({
          barcode: submitted.item_meta.barcode || '',
          brand: submitted.item_meta.brand || '',
          model: submitted.item_meta.model || '',
          variant: submitted.item_meta.variant || '',
          suggestedName: submitted.name || '',
          originalName: submitted.item_meta.original_name || '',
          confidence: submitted.item_meta.identification_confidence || 0,
          officialImage: submitted.item_meta.official_image_url || '',
        }).catch(() => {})
      }
    },
    onError: async (error, submitted) => {
      const mutationId = submitted?.client_mutation_id
      const kind = classifyScanError(error)
      if (mutationId) {
        await scanOutbox.update(mutationId, {
          status: kind === 'failed' ? 'failed' : 'retry',
          lastError: error?.message || String(error),
        }).catch(() => {})
        queryClient.setQueryData(itemsCacheKey, (previous) => mergeCreatedItem(previous, {
          ...submitted,
          id: `pending-${mutationId}`,
          cart_id: cartId,
          client_mutation_id: mutationId,
          sync_pending: kind !== 'failed',
          sync_status: kind === 'failed' ? 'failed' : 'retry',
        }))
        if (kind === 'failed') {
          setAddProductError(describeScanSaveError(error))
          return
        }
        setAddProductError('')
        setShowScanFlow(false)
        return
      }
      setAddProductError(error?.response?.data?.detail || error?.message || 'Impossible d’ajouter le produit.')
    },
  })

  useEffect(() => {
    const persistScanEntry = async (entry) => {
      const conversion = await resolveJpyEurRate(() => exchangeRatesService.jpyToEur())
      const payload = applyConversionToPayload({
        ...(entry.draftPayload || {}),
        item_meta: { ...(entry.draftPayload?.item_meta || {}) },
        client_mutation_id: entry.clientMutationId,
      }, conversion)
      const uploaded = entry.captureBlob
        ? await productService.uploadScanImage(entry.captureBlob)
        : null
      const uploadedAdditional = await Promise.all(
        (entry.additionalBlobs || []).map((photo, index) => (
          productService.uploadScanImage(
            photo.blob || photo,
            `scan-${photo.kind || 'additional'}-${index + 1}.jpg`,
          )
        )),
      )
      if (uploaded?.url) {
        payload.image_url = persistImageUrl(payload.item_meta.official_image_url)
        payload.image_key = uploaded.key || ''
        payload.item_meta.capture_image_key = uploaded.key || ''
        payload.item_meta.capture_image_url = uploaded.url
        payload.item_meta.original_capture_url = uploaded.url
        payload.item_meta.store_photo_url = uploaded.url
        payload.item_meta.images = {
          ...(payload.item_meta.images || {}),
          capture: uploaded.url,
          original_capture: uploaded.url,
          capture_key: uploaded.key || '',
          additional: uploadedAdditional.map((row) => row.url).filter(Boolean),
        }
      }
      const created = await itemsService.create(entry.cartId, payload)
      if (!isConfirmedScanSave(created)) {
        throw Object.assign(new Error('Enregistrement non confirmé'), { status: 503 })
      }
      if (String(entry.cartId) === String(cartId)) {
        queryClient.setQueryData(itemsCacheKey, (previous) => mergeCreatedItem(previous, {
          ...created,
          client_mutation_id: entry.clientMutationId,
        }))
      }
      if (payload.item_meta.identification_source || payload.item_meta.barcode) {
        void productService.confirmIdentification({
          barcode: payload.item_meta.barcode || '',
          brand: payload.item_meta.brand || '',
          model: payload.item_meta.model || '',
          variant: payload.item_meta.variant || '',
          suggestedName: payload.name || '',
          confidence: payload.item_meta.identification_confidence || 0,
        }).catch(() => {})
      }
      return created
    }

    const hydratePendingConversions = async () => {
      const current = queryClient.getQueryData(itemsCacheKey)
      if (!Array.isArray(current)) return
      const pendingFx = current.filter((item) => (
        item?.id
        && !String(item.id).startsWith('pending-')
        && conversionIsPending(item)
      ))
      if (!pendingFx.length) return
      const conversion = await resolveJpyEurRate(() => exchangeRatesService.jpyToEur())
      if (!(conversion?.rate > 0)) return
      for (const item of pendingFx) {
        const next = applyConversionToPayload(item, conversion)
        if (!(Number(next.price) > 0)) continue
        try {
          await itemsService.update(item.id, { price: next.price, item_meta: next.item_meta })
          queryClient.setQueryData(itemsCacheKey, (prev) => mergeUpdatedItem(prev, item.id, {
            price: next.price,
            item_meta: next.item_meta,
          }))
        } catch {
          // La conversion pourra être reprise plus tard.
        }
      }
    }

    const synchronizer = createScanOutboxSynchronizer({
      outbox: scanOutbox,
      submit: persistScanEntry,
    })
    scanSyncRef.current = synchronizer
    void scanOutbox.list().then((entries) => {
      queryClient.setQueryData(itemsCacheKey, (previous) => attachPendingScans(previous, entries, cartId))
    })
    void synchronizer.start().then(() => hydratePendingConversions()).catch(() => {})
    return () => {
      scanSyncRef.current = null
      synchronizer.stop()
    }
  // itemsCacheKey est dérivé de ces identifiants.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, currentUserId, queryClient])

  const updateMutation = useMutation({
    mutationFn: async ({ itemId, data }) => {
      if (String(itemId).startsWith('pending-')) {
        const mutationId = String(itemId).slice('pending-'.length)
        const entries = await scanOutbox.list().catch(() => [])
        const existing = entries.find((entry) => entry.clientMutationId === mutationId)
        const draftPayload = { ...(existing?.draftPayload || {}) }
        const meta = { ...(draftPayload.item_meta || {}) }
        if (data.name != null) draftPayload.name = data.name
        if (data.quantity != null) draftPayload.quantity = data.quantity
        if (data.link != null) draftPayload.link = data.link
        if (data.image_url != null) draftPayload.image_url = data.image_url
        if (data.item_meta && typeof data.item_meta === 'object') {
          Object.assign(meta, data.item_meta)
        }
        if (data.price != null) {
          draftPayload.price = data.price
        }
        draftPayload.item_meta = meta
        await scanOutbox.update(mutationId, {
          draftPayload,
          status: 'pending',
          nextRetryAt: null,
          lastError: '',
        })
        return { local: true, itemId, data: { ...draftPayload, id: itemId } }
      }
      return itemsService.update(itemId, data)
    },
    onSuccess: (_result, variables) => {
      const nextData = _result?.local ? _result.data : variables.data
      queryClient.setQueryData(itemsCacheKey, (prev) => mergeUpdatedItem(prev, variables.itemId, nextData))
      if (_result?.local) {
        void scanSyncRef.current?.sync()
      } else {
        queryClient.invalidateQueries({ queryKey: itemsCacheKey })
      }
      queryClient.invalidateQueries({ queryKey: ['semantic-search-dropdown'] })
      if (variables.source !== 'edit-modal') return
      const next = nextEditProductUiState({
        fromPrice: variables.previousPrice,
        toPrice: variables.data?.price,
      })
      setShowEditModal(false)
      setEditProductError('')
      if (next.priceRoll) {
        setPriceRoll({
          itemId: variables.itemId,
          from: next.priceRoll.from,
          to: next.priceRoll.to,
          key: `${variables.itemId}-${Date.now()}`,
        })
      }
    },
    onError: (error, variables) => {
      console.error('[UPDATE] Erreur lors de la mise à jour:', error)
      const detail = error?.response?.data?.detail || error?.message || 'Impossible de mettre à jour le produit. Veuillez réessayer.'
      if (variables?.source === 'edit-modal') {
        setEditProductError(detail)
        return
      }
      if (error.response?.status === 401) {
        addNotification({
          type: 'error',
          title: 'Erreur d\'authentification',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
          showToast: true
        })
      } else {
        addNotification({
          type: 'error',
          title: 'Erreur',
          message: detail,
          showToast: true
        })
      }
    },
  })
  const editSaving = updateMutation.isPending && updateMutation.variables?.source === 'edit-modal'

  const updateCartMutation = useMutation({
    mutationFn: ({ cartId, data }) => {
      console.log('Mutation: Mise à jour du panier', cartId, data)
      return cartsService.update(cartId, data)
    },
    onSuccess: async (result) => {
      console.log('[MUTATION] Mutation réussie, résultat:', result)
      console.log('[MUTATION] Budget dans la réponse:', result?.budget, 'Type:', typeof result?.budget)
      console.log('[MUTATION] Cart complet dans la réponse:', result?.cart)
      
      // Fermer le modal d'abord
      setShowBudgetModal(false)
      setBudgetValue('')
      
      // Si le backend retourne le budget directement, mettre à jour le cache immédiatement
      if (result?.budget !== undefined && result?.budget !== null) {
        console.log('[MUTATION] Mise à jour optimiste du cache avec budget:', result.budget)
        queryClient.setQueryData(['carts'], (oldCarts) => {
          if (!oldCarts) return oldCarts
          const updated = oldCarts.map(cart => 
            cart.id === cartId 
              ? { ...cart, budget: result.budget }
              : cart
          )
          console.log('[MUTATION] Cache mis à jour:', updated.map(c => ({ id: c.id, name: c.name, budget: c.budget })))
          return updated
        })
      }
      
      // Attendre un peu pour que Supabase mette à jour
      await new Promise(resolve => setTimeout(resolve, 1500))
      // Invalider et rafraîchir les données des paniers
      console.log('[MUTATION] Invalidation des queries carts...')
      await queryClient.invalidateQueries({ queryKey: ['carts'] })
      console.log('[MUTATION] Refetch des queries carts...')
      // Utiliser refetchCarts directement
      const refreshedCarts = await refetchCarts()
      console.log('[MUTATION] Carts après refetch:', refreshedCarts.data)
      if (refreshedCarts.data) {
        console.log('[MUTATION] Budgets après refresh:', refreshedCarts.data.map(c => ({ id: c.id, name: c.name, budget: c.budget, type: typeof c.budget })))
        const updatedCart = refreshedCarts.data.find(c => c.id === cartId)
        if (updatedCart) {
          console.log('[MUTATION] Cart mis à jour trouvé:', updatedCart)
          console.log('[MUTATION] Budget du cart trouvé:', updatedCart.budget, 'Type:', typeof updatedCart.budget)
        } else {
          console.log('[MUTATION] Cart non trouvé après refresh')
        }
      } else {
        console.log('[MUTATION] Aucune donnée dans le résultat du refresh')
      }
    },
    onError: (error) => {
      console.error('Erreur mise à jour budget:', error)
      notify.error('Erreur lors de la mise à jour du budget: ' + (error.message || 'Erreur inconnue'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (itemId) => itemsService.delete(itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: itemsCacheKey })
      const previousItems = queryClient.getQueryData(itemsCacheKey)
      queryClient.setQueryData(itemsCacheKey, (prev) => (
        Array.isArray(prev) ? prev.filter((item) => item.id !== itemId) : prev
      ))
      return { previousItems }
    },
    onError: (error, _itemId, context) => {
      if (context?.previousItems !== undefined) {
        queryClient.setQueryData(itemsCacheKey, context.previousItems)
      }
      const detail = error?.response?.data?.detail || error?.message || 'Impossible de supprimer le produit.'
      if (itemToDeleteRef.current) {
        setDeleteError(detail)
      } else {
        notify.error(detail)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemsCacheKey })
      queryClient.invalidateQueries({ queryKey: ['carts', currentUserId] })
      queryClient.invalidateQueries({ queryKey: ['semantic-search-dropdown'] })
      setItemToDelete(null)
      setDeleteError('')
    },
  })

  const moveToSavedMutation = useMutation({
    mutationFn: (itemId) => itemsService.moveToSaved(itemId),
    // Animation PUIS suppression optimiste
    onMutate: async (itemId) => {
      // Annuler TOUTES les requêtes en cours
      await queryClient.cancelQueries({ queryKey: ['items', cartId] })
      await queryClient.cancelQueries({ queryKey: ['carts'] })
      await queryClient.cancelQueries({ queryKey: savedKey })
      
      // Sauvegarder l'état précédent pour rollback
      const previousItems = queryClient.getQueryData(['items', cartId])
      
      // Déclencher l'animation
      setSavingItemId(itemId)
      
      // Retourner le contexte pour rollback
      return { previousItems, itemId }
    },
    onSuccess: (data, itemId, context) => {
      // Attendre la fin de l'animation avant de supprimer l'item
      setTimeout(() => {
        // Supprimer l'item du cache
        const currentItems = queryClient.getQueryData(['items', cartId])
        if (currentItems) {
          queryClient.setQueryData(['items', cartId], 
            currentItems.filter(item => item.id !== itemId)
          )
        }
        if (data) {
          queryClient.setQueryData(savedKey, (current) => {
            if (!Array.isArray(current)) return current
            if (current.some((entry) => entry.id === data.id)) return current
            return [data, ...current]
          })
        } else {
          queryClient.invalidateQueries({ queryKey: savedKey })
        }
        
        // Effacer l'animation
        setSavingItemId(null)
        
        // Vérifier si les notifications de sauvegarde sont activées
        const productSavedNotificationsEnabled = localStorage.getItem('productSavedNotifications') !== 'false'
        if (productSavedNotificationsEnabled) {
          addNotification({
            type: 'success',
            title: 'Produit sauvegardé avec succès !',
            message: '',
            showToast: true
          })
        }
      }, 600) // Durée de l'animation
    },
    onError: (error, itemId, context) => {
      // Annuler l'animation
      setSavingItemId(null)
      
      // En cas d'erreur, restaurer l'état précédent (rollback)
      if (context?.previousItems) {
        queryClient.setQueryData(['items', cartId], context.previousItems)
      }
      
      addNotification({
        type: 'error',
        title: t('error') || 'Erreur',
        message: error.message || 'Erreur lors de la mise de côté',
        showToast: true
      })
    },
  })

  // Fonction pour actualiser un prix avec la méthode sélectionnée
  const refreshPriceWithMethod = async (itemId, method) => {
    // Si aucune méthode n'est fournie, utiliser celle de l'état
    const selectedMethod = method || refreshMethod
    const item = items.find(i => i.id === itemId)
    if (!item?.link) {
      throw new Error('L\'item n\'a pas de lien')
    }
    
    const isAmazon = item.link.toLowerCase().includes('amazon')
    
    // Si méthode = 'auto', utiliser la logique par défaut
    if (selectedMethod === 'auto') {
      if (isAmazon) {
        console.log('[CartDetailPage] Produit Amazon détecté, utilisation du serveur avec ScraperAPI')
        try {
          return itemsService.refreshPrice(itemId, null, null, skipWhitelist, true) // forceScraperAPI = true pour Amazon
        } catch (serverError) {
          console.log('[CartDetailPage] Scraping serveur échoué, fallback extension:', serverError.message)
          try {
            const { extractProductViaExtensionAlt } = await import('../utils/extensionHelper')
            const productInfo = await extractProductViaExtensionAlt(item.link)
            if (productInfo && productInfo.price && productInfo.price > 0) {
              console.log('[CartDetailPage] Extraction via extension réussie (fallback):', productInfo)
              return itemsService.refreshPrice(itemId, null, productInfo, skipWhitelist)
            }
          } catch (extensionError) {
            console.log('[CartDetailPage] Extension non disponible:', extensionError.message)
          }
          throw serverError
        }
      } else {
        // PRIORITÉ 1 : Extension
        try {
          const { extractProductViaExtensionAlt } = await import('../utils/extensionHelper')
          console.log('[CartDetailPage] Tentative extraction via extension pour:', item.link)
          const productInfo = await extractProductViaExtensionAlt(item.link)
          if (productInfo && productInfo.price && productInfo.price > 0) {
            console.log('[CartDetailPage] Extraction via extension réussie:', productInfo)
            return itemsService.refreshPrice(itemId, null, productInfo, skipWhitelist)
          }
        } catch (extensionError) {
          console.log('[CartDetailPage] Extension non disponible ou erreur:', extensionError.message)
        }
        
        // PRIORITÉ 2 : Scraping client
        try {
          const { scrapeProductFromBrowser } = await import('../utils/clientScraper')
          const productInfo = await scrapeProductFromBrowser(item.link)
          if (!productInfo.error && productInfo.price && productInfo.price > 0) {
            console.log('[CartDetailPage] Scraping client réussi, envoi des infos au backend:', productInfo)
            return itemsService.refreshPrice(itemId, null, productInfo, skipWhitelist)
          }
        } catch (clientError) {
          console.log('[CartDetailPage] Erreur scraping client, fallback serveur:', clientError)
        }
        
        // PRIORITÉ 3 : Serveur
        console.log('[CartDetailPage] Tentative scraping serveur (fallback)')
        return itemsService.refreshPrice(itemId, null, null, skipWhitelist)
      }
    }
    
    // Méthode forcée
    if (selectedMethod === 'extension') {
      console.log('[CartDetailPage] Méthode forcée: Extension, skipWhitelist:', skipWhitelist)
      const { extractProductViaExtensionAlt } = await import('../utils/extensionHelper')
      const productInfo = await extractProductViaExtensionAlt(item.link)
      if (!productInfo || !productInfo.price || productInfo.price <= 0) {
        throw new Error('Impossible d\'extraire le prix via l\'extension')
      }
      console.log('[CartDetailPage] Envoi productInfo au backend avec skipWhitelist:', skipWhitelist)
      return itemsService.refreshPrice(itemId, null, productInfo, skipWhitelist)
    }
    
    if (selectedMethod === 'client') {
      console.log('[CartDetailPage] Méthode forcée: Scraping client')
      const { scrapeProductFromBrowser } = await import('../utils/clientScraper')
      const productInfo = await scrapeProductFromBrowser(item.link)
      if (productInfo.error || !productInfo.price || productInfo.price <= 0) {
        throw new Error(productInfo.error || 'Impossible d\'extraire le prix via le scraping client')
      }
      return itemsService.refreshPrice(itemId, null, productInfo, skipWhitelist)
    }
    
    if (selectedMethod === 'server') {
      console.log('[CartDetailPage] Méthode forcée: Serveur (ScraperAPI)')
      return itemsService.refreshPrice(itemId, null, null, skipWhitelist, true) // forceScraperAPI = true
    }
    
    throw new Error('Méthode d\'actualisation inconnue')
  }

  const runItemPriceRefresh = async (itemId, method) => {
    if (!beginItemRefresh(itemId)) {
      throw new Error('Actualisation déjà en cours pour cet article')
    }
    try {
      return await refreshPriceWithMethod(itemId, method)
    } finally {
      endItemRefresh(itemId)
    }
  }

  const refreshPriceMutation = useMutation({
    mutationFn: async (itemId) => {
      console.log('[CartDetailPage] 🔄 Début actualisation prix pour item:', itemId)
      return runItemPriceRefresh(itemId, refreshMethod)
    },
    retry: false,
    onSuccess: async (data) => {
      console.log('[CartDetailPage] 💰 Prix actualisé avec succès:', data)
      console.log('[CartDetailPage] 💰 Ancien prix:', data.old_price, '→ Nouveau prix:', data.new_price)

      if (!isValidScrapedPrice(data?.new_price)) {
        addNotification({
          type: 'error',
          title: 'Erreur',
          message: 'Prix invalide reçu. Le prix enregistré est conservé.',
          showToast: true
        })
        return
      }

      if (data.item_id) {
        queryClient.setQueryData(itemsCacheKey, (prev) => mergeUpdatedItem(prev, data.item_id, {
          price: data.new_price,
          last_price_check: data.last_price_check || new Date().toISOString(),
        }))
      }
      queryClient.invalidateQueries({ queryKey: ['carts'] })
      if (data.history_recorded && data.item_id) {
        queryClient.invalidateQueries({ queryKey: ['priceHistory', data.item_id] })
      }

      const ui = nextPriceRefreshUiState({
        oldPrice: data.old_price,
        newPrice: data.new_price,
        historyRecorded: data.history_recorded,
        historySkipped: data.history_skipped,
      })
      if (ui.priceRoll && data.item_id) {
        setPriceRoll({
          itemId: data.item_id,
          from: ui.priceRoll.from,
          to: ui.priceRoll.to,
          key: `${data.item_id}-${data.last_price_check || Date.now()}`,
        })
      }

      const priceChange = data.price_change || 0
      const message = ui.warning
        || (priceChange !== 0
          ? `Prix actualisé : ${data.old_price?.toFixed(2)}€ → ${data.new_price?.toFixed(2)}€ (${priceChange >= 0 ? '+' : ''}${priceChange.toFixed(2)}€)`
          : `Prix actualisé : ${data.new_price?.toFixed(2)}€ (pas de changement)`)
      addNotification({
        type: ui.warning ? 'warning' : 'success',
        title: ui.warning ? 'Prix actualisé' : 'Prix actualisé',
        message: message,
        showToast: true
      })
    },
    onError: (error) => {
      const described = describePriceRefreshError(error)
      console.log('[CartDetailPage] Erreur refreshPrice:', described.message)
      addNotification({
        type: 'error',
        title: described.title,
        message: described.message,
        showToast: true,
        duration: described.title === 'Limite du fournisseur' ? 8000 : 5000
      })
    },
  })

  const refreshAllPricesMutation = useMutation({
    retry: false,
    mutationFn: async () => {
      console.log('[CartDetailPage] Début de refreshAllPrices pour le panier:', cartId)
      
      // Récupérer tous les items avec un lien
      const itemsWithLinks = items.filter(item => item.link)
      console.log('[CartDetailPage] Items à actualiser:', itemsWithLinks.length)
      
      // Initialiser la progression
      setRefreshProgress({ current: 0, total: itemsWithLinks.length, isActive: true })
      
      if (itemsWithLinks.length === 0) {
        setRefreshProgress({ current: 0, total: 0, isActive: false })
        return {
          success: true,
          message: 'Aucun item avec lien à actualiser',
          total_items: 0,
          updated_items: 0,
          errors: 0
        }
      }
      
      // Actualiser chaque item avec la méthode sélectionnée
      let updatedItems = 0
      let errors = 0
      const errorDetails = []
      const priceChanges = [] // Pour stocker les fluctuations de prix
      
      for (let i = 0; i < itemsWithLinks.length; i++) {
        const item = itemsWithLinks[i]
        const oldPrice = item.price || 0
        if (!canStartItemRefresh(refreshingIdsRef.current, item.id)) {
          errors++
          errorDetails.push({
            item_id: item.id,
            item_name: item.name,
            error: 'Actualisation déjà en cours pour cet article',
          })
          setRefreshProgress({ current: i + 1, total: itemsWithLinks.length, isActive: true })
          continue
        }
        try {
          console.log('[CartDetailPage] Actualisation de l\'item:', item.id, item.name, 'avec méthode:', refreshMethod)
          const result = await runItemPriceRefresh(item.id, refreshMethod)
          if (!isValidScrapedPrice(result?.new_price)) {
            throw new Error('Prix invalide reçu. Le prix enregistré est conservé.')
          }
          updatedItems++
          
          // Capturer les changements de prix
          if (result && result.item_id) {
            const newPrice = result.new_price || 0
            const priceChange = result.price_change || 0
            priceChanges.push({
              item_name: item.name,
              old_price: oldPrice,
              new_price: newPrice,
              price_change: priceChange,
              item_id: item.id
            })
          }
          
          console.log('[CartDetailPage] Item actualisé avec succès:', item.id)
        } catch (error) {
          errors++
          const described = describePriceRefreshError(error)
          errorDetails.push({ item_id: item.id, item_name: item.name, error: described.message })
          console.error('[CartDetailPage] Erreur actualisation item:', item.id, described.message)
        }
        
        // Mettre à jour la progression
        setRefreshProgress({ current: i + 1, total: itemsWithLinks.length, isActive: true })
      }
      
      const result = {
        success: true,
        message: `${updatedItems}/${itemsWithLinks.length} prix mis à jour${errors > 0 ? ` (${errors} erreurs)` : ''}`,
        total_items: itemsWithLinks.length,
        updated_items: updatedItems,
        errors: errors,
        error_details: errorDetails,
        price_changes: priceChanges // Inclure les changements de prix
      }
      
      console.log('[CartDetailPage] Résultat refreshAllPrices:', result)
      return result
    },
    onMutate: () => {
      console.log('[CartDetailPage] onMutate - Notification de début')
      // Réinitialiser la progression
      setRefreshProgress({ current: 0, total: 0, isActive: true })
      // Notification de début - on stocke l'ID pour la supprimer après
      const notificationId = addNotification({
        type: 'info',
        title: 'Mise à jour en cours',
        message: 'Actualisation de tous les prix...',
        showToast: true,
        autoDismiss: false // Ne pas fermer automatiquement
      })
      // Stocker l'ID dans une variable pour pouvoir le supprimer après
      return { notificationId }
    },
    onSuccess: async (data, variables, context) => {
      // Animer la disparition de la barre de progression
      setRefreshProgress(prev => ({ ...prev, isExiting: true }))
      // Attendre la fin de l'animation avant de masquer complètement
      setTimeout(() => {
        setRefreshProgress({ current: 0, total: 0, isActive: false, isExiting: false })
      }, 300) // Durée de l'animation de disparition
      // Supprimer la notification de début si elle existe
      if (context?.notificationId) {
        console.log('[CartDetailPage] Suppression de la notification de début:', context.notificationId)
        removeNotification(context.notificationId)
      }
      console.log('[CartDetailPage] onSuccess - Données reçues:', data)
      
      // Vérifier que les données sont valides
      if (!data) {
        console.error('[CartDetailPage] Aucune donnée reçue dans onSuccess')
        addNotification({
          type: 'error',
          title: 'Erreur',
          message: 'Aucune donnée reçue du serveur',
          showToast: true
        })
        return
      }
      
      // Invalider et forcer le refetch immédiat des items
      console.log('[CartDetailPage] Invalidation et refetch des items...')
      await queryClient.invalidateQueries({ queryKey: ['items', cartId] })
      await refetchItems()
      console.log('[CartDetailPage] Items rechargés')
      
      // Construire le message de notification avec les statistiques
      const totalItems = data.total_items ?? 0
      const updatedItems = data.updated_items ?? 0
      const errors = data.errors ?? 0
      const elapsedTime = data.elapsed_time ?? 0
      const priceChanges = data.price_changes || []
      
      console.log('[CartDetailPage] Statistiques:', { totalItems, updatedItems, errors, elapsedTime, priceChanges })
      
      // Analyser les fluctuations de prix
      const increases = priceChanges.filter(p => p.price_change > 0)
      const decreases = priceChanges.filter(p => p.price_change < 0)
      const unchanged = priceChanges.filter(p => p.price_change === 0)
      const totalIncrease = increases.reduce((sum, p) => sum + p.price_change, 0)
      const totalDecrease = Math.abs(decreases.reduce((sum, p) => sum + p.price_change, 0))
      
      let message = ''
      let notificationType = 'success'
      let title = '✅ Mise à jour terminée'
      
      if (totalItems === 0) {
        // Message plus détaillé pour expliquer pourquoi aucun produit n'est mis à jour
        message = data.message || 'Aucun produit à mettre à jour automatiquement.\n\nPour mettre à jour un produit, il doit avoir un lien/URL vers la page du produit.\n\nAjoutez un lien à vos produits pour activer la mise à jour automatique des prix.'
        notificationType = 'info'
        title = 'ℹ️ Aucun produit à mettre à jour'
      } else if (updatedItems === totalItems && errors === 0) {
        // Tous les prix ont été mis à jour avec succès
        message = `🎉 Tous les prix ont été mis à jour avec succès !`
        if (elapsedTime > 0) {
          message += ` (${totalItems} produit${totalItems > 1 ? 's' : ''} en ${elapsedTime}s)`
        } else {
          message += ` (${totalItems} produit${totalItems > 1 ? 's' : ''})`
        }
        notificationType = 'success'
        title = '✅ Mise à jour réussie'
      } else {
        // Mise à jour partielle
        message = `${updatedItems} prix mis à jour sur ${totalItems}`
        if (errors > 0) {
          message += ` (${errors} erreur${errors > 1 ? 's' : ''})`
        }
        if (elapsedTime > 0) {
          message += ` en ${elapsedTime}s`
        }
        notificationType = updatedItems > 0 ? 'success' : 'warning'
        title = updatedItems > 0 ? '⚠️ Mise à jour partielle' : '❌ Mise à jour échouée'
      }
      
      // Ajouter le récapitulatif des fluctuations si des prix ont été mis à jour
      if (priceChanges.length > 0) {
        message += '\n\n📊 Récapitulatif des fluctuations :\n'
        
        if (increases.length > 0) {
          message += `\n📈 ${increases.length} hausse${increases.length > 1 ? 's' : ''} : +${totalIncrease.toFixed(2)}€`
          // Afficher les 3 plus importantes hausses
          const topIncreases = increases
            .sort((a, b) => b.price_change - a.price_change)
            .slice(0, 3)
          topIncreases.forEach(p => {
            message += `\n  • ${p.item_name}: ${p.old_price.toFixed(2)}€ → ${p.new_price.toFixed(2)}€ (+${p.price_change.toFixed(2)}€)`
          })
        }
        
        if (decreases.length > 0) {
          message += `\n\n📉 ${decreases.length} baisse${decreases.length > 1 ? 's' : ''} : -${totalDecrease.toFixed(2)}€`
          // Afficher les 3 plus importantes baisses
          const topDecreases = decreases
            .sort((a, b) => a.price_change - b.price_change)
            .slice(0, 3)
          topDecreases.forEach(p => {
            message += `\n  • ${p.item_name}: ${p.old_price.toFixed(2)}€ → ${p.new_price.toFixed(2)}€ (${p.price_change.toFixed(2)}€)`
          })
        }
        
        if (unchanged.length > 0) {
          message += `\n\n➡️ ${unchanged.length} prix stable${unchanged.length > 1 ? 's' : ''}`
        }
        
        // Bilan global
        const netChange = totalIncrease - totalDecrease
        if (netChange !== 0) {
          message += `\n\n💰 Bilan global : ${netChange >= 0 ? '+' : ''}${netChange.toFixed(2)}€`
        }
      }
      
      addNotification({
        type: notificationType,
        title: title,
        message: message,
        showToast: true,
        autoDismiss: true,
        duration: priceChanges.length > 0 ? 12000 : undefined // Notification plus longue si récapitulatif
      })
    },
    onError: (error, variables, context) => {
      // Animer la disparition de la barre de progression
      setRefreshProgress(prev => ({ ...prev, isExiting: true }))
      // Attendre la fin de l'animation avant de masquer complètement
      setTimeout(() => {
        setRefreshProgress({ current: 0, total: 0, isActive: false, isExiting: false })
      }, 300) // Durée de l'animation de disparition
      // Supprimer la notification de début si elle existe
      if (context?.notificationId) {
        console.log('[CartDetailPage] Suppression de la notification de début (erreur):', context.notificationId)
        removeNotification(context.notificationId)
      }
      
      console.error('[CartDetailPage] Erreur refreshAllPrices:', error)
      
      const described = describePriceRefreshError(error)
      let errorMessage = described.message
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'La mise à jour a été interrompue. Les prix déjà enregistrés sont conservés.'
      }
      
      addNotification({
        type: 'error',
        title: described.title,
        message: errorMessage,
        showToast: true
      })
    },
  })

  // État pour l'animation de succès d'import
  const [importSuccessItem, setImportSuccessItem] = useState(null)
  
  // État pour l'animation de mise de côté
  const [savingItemId, setSavingItemId] = useState(null)

  const moveFromLibraryMutation = useMutation({
    mutationFn: (savedId) => savedItemsService.moveToCart(savedId, cartId),
    // Mise à jour optimiste AVEC animation
    onMutate: async (savedId) => {
      // Annuler les requêtes en cours
      await queryClient.cancelQueries({ queryKey: savedKey })
      await queryClient.cancelQueries({ queryKey: ['items', cartId] })
      
      // Sauvegarder l'item pour l'animation et le rollback
      const previousSavedItems = queryClient.getQueryData(savedKey)
      const previousItems = queryClient.getQueryData(['items', cartId])
      const item = previousSavedItems?.find(i => i.id === savedId)
      
      // Déclencher l'animation de succès AVANT de supprimer l'item
      if (item) {
        setImportSuccessItem(item)
      }
      
      return { previousSavedItems, previousItems, item }
    },
    onSuccess: (newItem, savedId, context) => {
      console.log('[CartDetailPage] ✅ Import réussi, nouvel item:', newItem)
      
      // Ajouter IMMÉDIATEMENT le nouvel item au cache du panier
      const currentItems = queryClient.getQueryData(['items', cartId])
      if (currentItems && newItem) {
        queryClient.setQueryData(['items', cartId], [...currentItems, newItem])
        console.log('[CartDetailPage] 📦 Item ajouté au cache du panier')
      }
      
      // Afficher la notification de succès si activée
      const productImportedNotificationsEnabled = localStorage.getItem('productImportedNotifications') !== 'false'
      if (productImportedNotificationsEnabled) {
        addNotification({
          type: 'success',
          title: 'Produit importé avec succès !',
          message: '',
          showToast: true
        })
      }
      
      // Attendre que l'animation se termine avant de supprimer l'item de la bibliothèque
      setTimeout(() => {
        // Supprimer l'item de la bibliothèque
        const currentSavedItems = queryClient.getQueryData(savedKey)
        if (currentSavedItems) {
          queryClient.setQueryData(savedKey, 
            currentSavedItems.filter(i => i.id !== savedId)
          )
        }
        
        // Effacer l'animation
        setImportSuccessItem(null)
      }, 800) // Délai pour l'animation (0.8 secondes)
    },
    onError: (error, savedId, context) => {
      // Rollback en cas d'erreur
      setImportSuccessItem(null)
      if (context?.previousItems) {
        queryClient.setQueryData(['items', cartId], context.previousItems)
      }
      if (context?.previousSavedItems) {
        queryClient.setQueryData(savedKey, context.previousSavedItems)
      }
      addNotification({
        type: 'error',
        title: t('error') || 'Erreur',
        message: error.message || 'Erreur lors de l\'import du produit',
        showToast: true
      })
    },
  })

  const deleteFromLibraryMutation = useMutation({
    mutationFn: (savedId) => savedItemsService.delete(savedId),
    // Mise à jour optimiste
    onMutate: async (savedId) => {
      await queryClient.cancelQueries({ queryKey: savedKey })
      
      const previousSavedItems = queryClient.getQueryData(savedKey)
      
      // Supprimer immédiatement du cache
      if (previousSavedItems) {
        queryClient.setQueryData(savedKey, 
          previousSavedItems.filter(i => i.id !== savedId)
        )
      }
      
      return { previousSavedItems }
    },
    onSuccess: async () => {
      addNotification({
        type: 'success',
        title: t('productDeleted') || 'Produit supprimé',
        message: t('productDeletedFromLibrary') || 'Produit supprimé de la bibliothèque avec succès',
        showToast: true
      })
    },
    onError: (error, savedId, context) => {
      // Rollback en cas d'erreur
      if (context?.previousSavedItems) {
        queryClient.setQueryData(savedKey, context.previousSavedItems)
      }
      addNotification({
        type: 'error',
        title: t('error') || 'Erreur',
        message: error.message || 'Erreur lors de la suppression',
        showToast: true
      })
    },
  })

  const filteredItems = applyCartFilters(items, {
    searchTerm,
    filters: cartFilters,
    priceSort: cartPriceSort,
  })
  const activeFilterCount = countActiveFilters(cartFilters)
  const hasActiveFilters = activeFilterCount > 0 || Boolean(searchTerm.trim())
  
  const resetFilters = () => {
    setCartFilters(emptyCartFilters())
  }

  const total = items.reduce((sum, item) => {
    return sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)
  }, 0)
  const mobileJpyTotal = isPhoneLayout ? observedJpyTotal(items) : 0

  // Helper pour les notifications
  const notify = {
    success: (message, title = null) => {
      addNotification({
        type: 'success',
        title: title || t('success') || 'Succès',
        message,
        showToast: true
      })
    },
    error: (message, title = null) => {
      addNotification({
        type: 'error',
        title: title || t('error') || 'Erreur',
        message,
        showToast: true
      })
    },
    warning: (message, title = null) => {
      addNotification({
        type: 'warning',
        title: title || t('warning') || 'Attention',
        message,
        showToast: true
      })
    },
    info: (message, title = null) => {
      addNotification({
        type: 'info',
        title: title || t('info') || 'Information',
        message,
        showToast: true
      })
    },
    confirm: (message, title = null) => {
      return new Promise((resolve) => {
        setConfirmModal({
          isOpen: true,
          title: title || t('confirm') || 'Confirmation',
          message,
          onConfirm: () => {
            resolve(true)
            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
          },
          onCancel: () => {
            resolve(false)
            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
          }
        })
      })
    }
  }

  // Référence pour suivre les notifications de budget déjà créées
  const budgetNotificationRef = useRef({ 
    lastBudget: null, 
    lastTotal: null, 
    lastPercentage: null,
    initialized: false 
  })

  useEffect(() => {
    budgetNotificationRef.current = {
      lastBudget: null,
      lastTotal: null,
      lastPercentage: null,
      initialized: false,
    }
  }, [cartId])

  // Notifications de budget
  useEffect(() => {
    if (!itemsLoaded || !currentCart?.budget) return
    
    const budgetNum = typeof currentCart.budget === 'string' ? parseFloat(currentCart.budget) : Number(currentCart.budget)
    if (isNaN(budgetNum) || budgetNum <= 0) return

    const percentage = (total / budgetNum) * 100
    const remaining = budgetNum - total

    const prev = budgetNotificationRef.current

    // Si c'est la première fois, juste initialiser sans créer de notification
    if (!prev.initialized) {
      budgetNotificationRef.current = {
        lastBudget: budgetNum,
        lastTotal: total,
        lastPercentage: percentage,
        initialized: true
      }
      return
    }

    // Vérifier si les valeurs ont changé de manière significative
    const budgetChanged = prev.lastBudget !== budgetNum
    const totalChanged = Math.abs(prev.lastTotal - total) > 0.01
    const percentageChanged = Math.abs(prev.lastPercentage - percentage) > 1

    // Ne créer une notification que si :
    // 1. Le budget ou le total a changé de manière significative
    // 2. ET que le statut a changé (passage sous/au-dessus de 80%, dépassement, etc.)
    const wasOverBudget = prev.lastPercentage >= 100
    const isOverBudget = percentage >= 100
    const wasCloseToBudget = prev.lastPercentage >= 80 && prev.lastPercentage < 100
    const isCloseToBudget = percentage >= 80 && percentage < 100

    // Vérifier si les notifications de budget sont activées
    const budgetNotificationsEnabled = localStorage.getItem('budgetExceededNotifications') !== 'false'
    
    // Montants formatés en français, pourcentage cohérent avec le reste à dépenser
    const notice = budgetNoticeText({ total, budget: budgetNum })

    // Notification si budget dépassé (seulement si changement de statut)
    if (budgetNotificationsEnabled && notice?.type === 'urgent' && (!wasOverBudget || (budgetChanged || totalChanged))) {
      addNotification({
        type: 'urgent',
        title: t('budgetExceeded') || 'Budget dépassé',
        message: notice.message,
        showToast: true
      })
    }
    // Notification si budget approche (80-100%) - seulement si changement de statut
    else if (budgetNotificationsEnabled && notice?.type === 'warning' && percentage < 100 && (!wasCloseToBudget || (budgetChanged || totalChanged))) {
      addNotification({
        type: 'warning',
        title: t('budgetWarning') || 'Budget proche',
        message: notice.message,
        showToast: true
      })
    }

    // Mettre à jour les références
    budgetNotificationRef.current = {
      lastBudget: budgetNum,
      lastTotal: total,
      lastPercentage: percentage,
      initialized: true
    }
  }, [itemsLoaded, total, currentCart?.budget, addNotification, t])

  const handleExtractProduct = async (url) => {
    try {
      const response = await productService.extractFromUrl(url)
      if (!response?.success || !response.product) {
        throw new Error('Impossible d’extraire les informations du produit.')
      }
      return { ...response.product, link: url }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Impossible d’extraire les informations du produit.'
      const isLuxurySite = /louisvuitton|dior|gucci|chanel|hermes/.test(String(url).toLowerCase())
      const isBlocked = /403|forbidden|bloque/.test(String(errorMessage).toLowerCase())
      if (isBlocked || isLuxurySite) {
        throw new Error('Ce site bloque les accès automatisés. Ouvrez la page du produit et utilisez l’extension pour l’importer.')
      }
      throw new Error(errorMessage)
    }
  }

  const retryScanItem = async (item) => {
    const mutationId = item.client_mutation_id || String(item.id || '').replace(/^pending-/, '')
    if (!mutationId) return
    await scanOutbox.update(mutationId, {
      status: 'pending',
      nextRetryAt: null,
      lastError: '',
    }).catch(() => {})
    queryClient.setQueryData(itemsCacheKey, (prev) => mergeCreatedItem(prev, {
      ...item,
      id: `pending-${mutationId}`,
      client_mutation_id: mutationId,
      sync_pending: true,
      sync_status: 'pending',
      sync_error: '',
    }))
    await scanSyncRef.current?.sync()
  }

  const handleEditItem = (item) => {
    editTriggerRef.current = document.activeElement
    editSnapshotRef.current = { id: item.id, price: Number(item.price) || 0 }
    setEditProductError('')
    setEditingItem({ ...item })
    setShowEditModal(true)
  }

  const openItemActionsMenu = (event, item) => {
    event.preventDefault()
    event.stopPropagation()
    if (selectionMode || selectionModeActiveRef.current) {
      if (selectionMode) toggleItemSelection(item.id)
      return
    }
    if (openGridMenuId === item.id) {
      setOpenGridMenuId(null)
      return
    }
    gridMenuAnchorRef.current = event.currentTarget
    gridMenuItemRef.current = item
    setGridMenuArrow('down')
    setOpenGridMenuId(item.id)
  }

  const handleUpdateItem = (itemId, field, value) => {
    updateMutation.mutate({ itemId, data: { [field]: value } })
  }

  const handleImageClick = async (item) => {
    const gallery = readItemGallery(item)
    let slides = gallery.slides
    if (!slides.length) {
      const resolved = resolveCardImage(item)
      let url = resolved.url
      if (!url && resolved.key) {
        url = await itemsService.signedMedia(resolved.key)
      }
      if (url) slides = [{ src: url, label: 'Image du produit' }]
    }
    if (!slides.length) return
    setSelectedImage(slides)
    setShowImageModal(true)
  }

  const handleItemImageError = useCallback((event, item) => {
    if (event?.currentTarget) {
      event.currentTarget.onerror = null
      event.currentTarget.src = '/Icon photo size select actual.png'
    }
    if (!item?.id) return
    if (imageErrorItemsRef.current.has(item.id)) return
    imageErrorItemsRef.current.add(item.id)
    queryClient.invalidateQueries(['items', cartId])
    setTimeout(() => {
      imageErrorItemsRef.current.delete(item.id)
    }, 2000)
  }, [cartId, queryClient])

  const clearItemLongPressTimer = useCallback(() => {
    const state = longPressRef.current
    if (state.timer) {
      window.clearTimeout(state.timer)
      state.timer = 0
    }
  }, [])

  const cancelItemLongPressGesture = useCallback(() => {
    const state = longPressRef.current
    if (state.timer) {
      window.clearTimeout(state.timer)
      state.timer = 0
    }
    state.cancelled = true
    document.querySelector('.item-card.long-pressing')?.classList.remove('long-pressing')
  }, [])

  const handleItemPointerDown = (itemId, event) => {
    if (selectionMode) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    if (!isItemLongPressTarget(event.target)) return

    const cardElement = event.currentTarget?.classList?.contains('item-card')
      ? event.currentTarget
      : event.target.closest('.item-card')
    const state = longPressRef.current
    clearItemLongPressTimer()
    state.itemId = itemId
    state.x = event.clientX
    state.y = event.clientY
    state.cancelled = false
    state.activated = false
    state.pointerId = event.pointerId
    longPressActivatedRef.current = false
    cardElement?.classList.add('long-pressing')

    state.timer = window.setTimeout(() => {
      if (state.cancelled || state.itemId !== itemId) return
      state.timer = 0
      state.activated = true
      longPressActivatedRef.current = true
      setSelectionMode(true)
      selectionModeActiveRef.current = true
      setSelectedItems(new Set([itemId]))
      recentlySelectedByLongPressRef.current.add(itemId)
      if (navigator.vibrate) navigator.vibrate(50)
      window.setTimeout(() => {
        longPressActivatedRef.current = false
        cardElement?.classList.remove('long-pressing')
        window.setTimeout(() => {
          recentlySelectedByLongPressRef.current.delete(itemId)
        }, 1000)
      }, 400)
    }, ITEM_LONG_PRESS_MS)
  }

  const handleItemPointerMove = (event) => {
    const state = longPressRef.current
    if (state.cancelled || state.pointerId !== event.pointerId) return
    if (!state.timer && !state.itemId) return
    if (pointerMovedBeyondThreshold(state, { x: event.clientX, y: event.clientY })) {
      cancelItemLongPressGesture()
    }
  }

  const handleItemPointerUp = (event) => {
    const state = longPressRef.current
    if (state.pointerId != null && event.pointerId !== state.pointerId) return
    const activated = state.activated
    clearItemLongPressTimer()
    state.itemId = null
    state.pointerId = null
    event.currentTarget?.classList?.remove('long-pressing')
    if (activated) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const toggleItemSelection = (itemId) => {
    // Si cet item vient d'être sélectionné par long press, ne pas permettre de le désélectionner immédiatement
    if (recentlySelectedByLongPressRef.current.has(itemId)) {
      // Ne pas désélectionner si c'est le seul élément sélectionné
      if (selectedItems.size === 1 && selectedItems.has(itemId)) {
        return
      }
      // Si on veut désélectionner un item récemment sélectionné mais qu'il y en a d'autres,
      // on peut le faire, mais on le retire de la liste des items récemment sélectionnés
      recentlySelectedByLongPressRef.current.delete(itemId)
    }
    
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      // Ne désactiver le mode sélection que si l'utilisateur a explicitement désélectionné tous les éléments
      // ET que ce n'est pas juste après l'activation du long press
      if (newSet.size === 0 && !longPressActivatedRef.current) {
        setSelectionMode(false)
        selectionModeActiveRef.current = false // Réinitialiser la ref
        recentlySelectedByLongPressRef.current.clear() // Nettoyer la liste
      }
      return newSet
    })
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    selectionModeActiveRef.current = false
    setSelectedItems(new Set())
    clearItemLongPressTimer()
    longPressRef.current.activated = false
    longPressRef.current.cancelled = true
    longPressRef.current.itemId = null
    longPressActivatedRef.current = false
    recentlySelectedByLongPressRef.current.clear()
  }

  useEffect(() => {
    const onScroll = () => {
      if (longPressRef.current.timer) cancelItemLongPressGesture()
    }
    const onSelectStart = (event) => {
      if (event.target?.closest?.('.item-card.long-pressing')) {
        event.preventDefault()
      }
    }
    document.addEventListener('scroll', onScroll, true)
    document.addEventListener('pointercancel', cancelItemLongPressGesture, true)
    document.addEventListener('selectstart', onSelectStart, true)
    window.addEventListener('pagehide', cancelItemLongPressGesture)
    return () => {
      document.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('pointercancel', cancelItemLongPressGesture, true)
      document.removeEventListener('selectstart', onSelectStart, true)
      window.removeEventListener('pagehide', cancelItemLongPressGesture)
      clearItemLongPressTimer()
    }
  }, [cancelItemLongPressGesture, clearItemLongPressTimer])


  // Actions du dock
  const handleDockOpenLinks = () => {
    const selectedItemsArray = Array.from(selectedItems).map(id => items.find(item => item.id === id)).filter(Boolean)
    const itemsWithLinks = selectedItemsArray.filter(item => item.link && item.link.trim() !== '')
    
    if (itemsWithLinks.length === 0) {
      notify.warning('Aucun produit sélectionné n\'a de lien')
      return
    }
    
    // Essayer d'ouvrir tous les liens
    // Si le navigateur bloque les popups, on affichera un message
    let openedCount = 0
    itemsWithLinks.forEach((item, index) => {
      try {
        // Valider l'URL avant d'ouvrir (protection XSS)
        const safeUrl = sanitizeUrl(item.link)
        if (!safeUrl) {
          console.warn(`[CartDetailPage] URL invalide ignorée: ${item.link}`)
          return
        }
        const newWindow = window.open(safeUrl, `_blank_${index}`, 'noopener,noreferrer')
        if (newWindow) {
          openedCount++
        }
      } catch (e) {
        console.error('Erreur ouverture lien:', e)
      }
    })
    
    // Si aucun lien n'a pu être ouvert, afficher un message avec les liens
    if (openedCount === 0 && itemsWithLinks.length > 0) {
      const linksText = itemsWithLinks.map(item => item.link).join('\n')
      const message = `Votre navigateur bloque les popups multiples.\n\nVoici les ${itemsWithLinks.length} lien(s) sélectionné(s) :\n\n${linksText}\n\nSouhaitez-vous copier ces liens dans le presse-papier ?`
      
      notify.confirm(message).then((confirmed) => {
        if (confirmed) {
          navigator.clipboard.writeText(linksText).then(() => {
            notify.success('Liens copiés dans le presse-papier !')
          }).catch(() => {
            notify.error('Impossible de copier dans le presse-papier. Voici les liens :\n\n' + linksText)
          })
        }
      })
    } else if (openedCount < itemsWithLinks.length) {
      notify.info(`${openedCount} lien(s) ouvert(s) sur ${itemsWithLinks.length}. Votre navigateur peut bloquer les popups multiples. Autorisez les popups pour ce site pour ouvrir tous les liens.`)
    }
    
    exitSelectionMode()
  }

  const handleDockCopy = () => {
    const selectedItemsArray = Array.from(selectedItems).map(id => items.find(item => item.id === id)).filter(Boolean)
    setCopiedItems(selectedItemsArray.map(item => ({
      name: item.name,
      link: item.link || '',
      price: item.price || 0,
      quantity: item.quantity || 1,
      promo: item.promo || '',
      certainty_label: item.certainty_label || 'Faible',
      image_url: item.image_url || '',
      image_key: item.image_key || ''
    })))
      notify.success(`${selectedItemsArray.length} produit(s) copié(s) ! Vous pouvez maintenant les coller dans un autre panier.`)
    exitSelectionMode()
  }

  const handleDockPaste = async (targetCartId) => {
    if (copiedItems.length === 0) {
      notify.warning('Aucun produit copié')
      return
    }
    
    try {
      for (const itemData of copiedItems) {
        await itemsService.create(targetCartId, itemData)
      }
      queryClient.invalidateQueries(['items', targetCartId])
      notify.success(`${copiedItems.length} produit(s) collé(s) !`)
    } catch (error) {
      console.error('Erreur collage:', error)
      notify.error('Erreur lors du collage des produits')
    }
  }

  // Fonction pour coller dans le panier actuel
  const handlePaste = async () => {
    // Récupérer les items copiés depuis localStorage au cas où
    let itemsToPaste = copiedItems
    if (itemsToPaste.length === 0) {
      try {
        const saved = localStorage.getItem('copiedItems')
        if (saved) {
          itemsToPaste = JSON.parse(saved)
          setCopiedItems(itemsToPaste)
        }
      } catch (e) {
        console.error('Erreur lecture copiedItems:', e)
      }
    }
    
    if (itemsToPaste.length === 0) {
      notify.warning('Aucun produit copié')
      return
    }
    
    try {
      for (const itemData of itemsToPaste) {
        await itemsService.create(cartId, itemData)
      }
      queryClient.invalidateQueries(['items', cartId])
      notify.success(`${itemsToPaste.length} produit(s) collé(s) dans ce panier !`)
    } catch (error) {
      console.error('Erreur collage:', error)
      notify.error('Erreur lors du collage des produits')
    }
  }

  const handleDockMoveToSaved = () => {
    const selectedItemsArray = Array.from(selectedItems)
    selectedItemsArray.forEach(itemId => {
      moveToSavedMutation.mutate(itemId)
    })
    exitSelectionMode()
  }

  const handleDockDelete = async () => {
    const confirmed = await notify.confirm(`Supprimer ${selectedItems.size} produit(s) sélectionné(s) ?`)
    if (!confirmed) {
      return
    }
    const selectedItemsArray = Array.from(selectedItems)
    selectedItemsArray.forEach(itemId => {
      deleteMutation.mutate(itemId)
    })
    exitSelectionMode()
  }


  // Fonction pour télécharger une image depuis une URL
  const downloadImageAsBlob = async (url) => {
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Erreur lors du téléchargement de l\'image')
      const blob = await response.blob()
      return blob
    } catch (error) {
      console.error('Erreur téléchargement image:', error)
      return null
    }
  }

  // Export JSON avec images
  const handleExportJSON = async () => {
    if (!currentCart || items.length === 0) {
      notify.warning('Le panier est vide ou introuvable')
      return
    }

    try {
      const zip = new JSZip()
      const cartData = {
        name: currentCart.name,
        created_at: currentCart.created_at,
        updated_at: currentCart.updated_at,
        items: items.map(item => ({
          name: item.name,
          link: item.link || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          promo: item.promo || '',
          certainty_label: item.certainty_label || 'Faible',
          image_filename: item.image_key ? `${item.id}_${item.image_key.split('/').pop()}` : null
        }))
      }

      // Ajouter le JSON
      zip.file('cart.json', JSON.stringify(cartData, null, 2))

      // Télécharger et ajouter les images
      const imagesFolder = zip.folder('images')
      for (const item of items) {
        if (item.image_url) {
          const blob = await downloadImageAsBlob(item.image_url)
          if (blob) {
            const filename = item.image_key ? `${item.id}_${item.image_key.split('/').pop()}` : `${item.id}.png`
            imagesFolder.file(filename, blob)
          }
        }
      }

      // Générer et télécharger le ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentCart.name.replace(/[^a-z0-9]/gi, '_')}_export.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      notify.success('Export JSON réussi !')
    } catch (error) {
      console.error('Erreur export JSON:', error)
      notify.error('Erreur lors de l\'export JSON')
    }
  }

  // Export CSV avec images
  const handleExportCSV = async () => {
    if (!currentCart || items.length === 0) {
      notify.warning('Le panier est vide ou introuvable')
      return
    }

    try {
      const zip = new JSZip()
      
            // Créer le CSV avec format Excel optimisé (point-virgule pour Excel français)
      const delimiter = ';'
      const csvRows = []
      
      // En-têtes avec métadonnées du panier
      csvRows.push('Export du Panier')
      csvRows.push('')
      csvRows.push(`Nom du panier;${(currentCart.name || '').replace(/;/g, ',')}`)
      csvRows.push(`Date de création;${currentCart.created_at ? new Date(currentCart.created_at).toLocaleDateString('fr-FR') : 'N/A'}`)
      csvRows.push(`Dernière mise à jour;${currentCart.updated_at ? new Date(currentCart.updated_at).toLocaleDateString('fr-FR') : 'N/A'}`)
      csvRows.push('')
      csvRows.push('Produits')
      csvRows.push('')
      
      // En-têtes des colonnes
      const headers = ['N°', 'Nom du produit', 'Prix unitaire (€)', 'Quantité', 'Prix total (€)', 'Promotion', 'Niveau de certitude', 'Lien', 'Image']
      csvRows.push(headers.join(delimiter))
      
      // Calculer le total
      let totalPanier = 0
      
      // Données des produits
      items.forEach((item, index) => {
        const prixUnitaire = parseFloat(item.price || 0)
        const quantite = parseInt(item.quantity || 1)
        const prixTotal = prixUnitaire * quantite
        totalPanier += prixTotal
        
        const imageFilename = item.image_key ? `${item.id}_${item.image_key.split('/').pop()}` : ''
        
        const row = [
          index + 1,
          (item.name || '').replace(/;/g, ',').replace(/"/g, '""'),
          prixUnitaire.toFixed(2).replace('.', ','),
          quantite,
          prixTotal.toFixed(2).replace('.', ','),
          (item.promo || '').replace(/;/g, ',').replace(/"/g, '""'),
          (item.certainty_label || 'Faible').replace(/;/g, ','),
          (item.link || '').replace(/;/g, ','),
          imageFilename ? `images/${imageFilename}` : ''
        ]
        csvRows.push(row.join(delimiter))
      })
      
      // Ligne de total
      csvRows.push('')
      csvRows.push(`TOTAL PANIER;${totalPanier.toFixed(2).replace('.', ',')} €`)
      csvRows.push(`Nombre de produits;${items.length}`)
      
      // Ajouter BOM UTF-8 pour Excel (assure l'encodage correct)
      const csvContent = '\uFEFF' + csvRows.join('\n')
      zip.file('cart.csv', csvContent)

      // Télécharger et ajouter les images
      const imagesFolder = zip.folder('images')
      for (const item of items) {
        if (item.image_url) {
          const blob = await downloadImageAsBlob(item.image_url)
          if (blob) {
            const filename = item.image_key ? `${item.id}_${item.image_key.split('/').pop()}` : `${item.id}.png`
            imagesFolder.file(filename, blob)
          }
        }
      }

      // Ajouter aussi un fichier JSON pour référence
      const cartData = {
        name: currentCart.name,
        created_at: currentCart.created_at,
        updated_at: currentCart.updated_at,
      }
      zip.file('cart_info.json', JSON.stringify(cartData, null, 2))

      // Générer et télécharger le ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentCart.name.replace(/[^a-z0-9]/gi, '_')}_export.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      notify.success('Export CSV réussi !')
    } catch (error) {
      console.error('Erreur export CSV:', error)
      notify.error('Erreur lors de l\'export CSV')
    }
  }

  // Export PDF avec liens interactifs et images
  const handleExportPDF = async () => {
    if (!currentCart || items.length === 0) {
      notify.warning('Le panier est vide ou introuvable')
      return
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape', // Format paysage pour avoir plus d'espace horizontal
        unit: 'mm',
        format: 'a4'
      })

      // Couleurs
      const headerColor = [240, 240, 240]
      const textColor = [0, 0, 0]

      // En-tête du document
      doc.setFontSize(20)
      doc.setTextColor(...textColor)
      doc.setFont('helvetica', 'bold')
      doc.text('Export du Panier', 105, 20, { align: 'center' })

      // Informations du panier
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text(`Nom du panier: ${currentCart.name || 'Sans nom'}`, 14, 35)
      
      if (currentCart.created_at) {
        const createdDate = new Date(currentCart.created_at).toLocaleDateString('fr-FR')
        doc.text(`Date de création: ${createdDate}`, 14, 42)
      }
      
      if (currentCart.updated_at) {
        const updatedDate = new Date(currentCart.updated_at).toLocaleDateString('fr-FR')
        doc.text(`Dernière mise à jour: ${updatedDate}`, 14, 49)
      }

      // Préparer les données pour le tableau avec liens interactifs
      // Stocker les URLs des liens et des images pour les ajouter après
      const linksData = []
      const imagesData = []
      
      const tableData = items.map((item, index) => {
        const prixUnitaire = parseFloat(item.price || 0)
        const quantite = parseInt(item.quantity || 1)
        const prixTotal = prixUnitaire * quantite
        const link = item.link || ''
        
        // Stocker les données de lien et d'image pour les traiter après
        if (link) {
          linksData.push({ rowIndex: index, link: link })
        }
        if (item.image_url) {
          imagesData.push({ rowIndex: index, imageUrl: item.image_url })
        }
        
        // Pour la colonne lien, afficher le texte complet (autoTable gérera le retour à la ligne)
        const linkText = link || '-'
        
        return [
          index + 1,
          item.name || 'Sans nom',
          `${prixUnitaire.toFixed(2)} €`,
          quantite,
          `${prixTotal.toFixed(2)} €`,
          item.promo || '-',
          item.certainty_label || 'Faible',
          linkText
        ]
      })

      // Calculer le total
      const totalPanier = items.reduce((sum, item) => {
        return sum + (parseFloat(item.price || 0) * (parseInt(item.quantity || 1)))
      }, 0)

      // Télécharger toutes les images d'abord avant de créer le tableau
      const imageResultsMap = new Map()
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.image_url) {
          try {
            const blob = await downloadImageAsBlob(item.image_url)
            if (blob) {
              const reader = new FileReader()
              const imageDataUrl = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(blob)
              })
              
              let imageFormat = 'JPEG'
              if (blob.type === 'image/png') {
                imageFormat = 'PNG'
              } else if (blob.type === 'image/jpeg' || blob.type === 'image/jpg') {
                imageFormat = 'JPEG'
              } else if (blob.type === 'image/gif') {
                imageFormat = 'GIF'
              } else if (blob.type === 'image/webp') {
                imageFormat = 'PNG'
              }
              
              imageResultsMap.set(i, { imageDataUrl, imageFormat })
            }
          } catch (error) {
            console.error(`Erreur téléchargement image ${item.name}:`, error)
          }
        }
      }

      // Créer le tableau avec autoTable
      // Ajuster les largeurs pour que le lien soit visible en entier
      autoTable(doc, {
        startY: 60,
        head: [['N°', 'Image', 'Nom du produit', 'Prix unitaire', 'Quantité', 'Prix total', 'Promotion', 'Certitude', 'Lien']],
        body: tableData.map((row, index) => {
          // Ajouter une cellule vide pour l'image (sera remplie après)
          return ['', ...row] // Insérer une cellule vide au début pour l'image
        }),
        theme: 'grid',
        headStyles: {
          fillColor: headerColor,
          textColor: textColor,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 9
        },
        bodyStyles: {
          textColor: textColor,
          halign: 'left',
          fontSize: 7
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 }, // N°
          1: { halign: 'center', cellWidth: 25 }, // Image
          2: { cellWidth: 50, overflow: 'linebreak' }, // Nom
          3: { halign: 'right', cellWidth: 22 }, // Prix unitaire
          4: { halign: 'center', cellWidth: 15 }, // Quantité
          5: { halign: 'right', cellWidth: 22 }, // Prix total
          6: { cellWidth: 25, overflow: 'linebreak' }, // Promotion
          7: { halign: 'center', cellWidth: 22 }, // Certitude
          8: { halign: 'left', cellWidth: 100, cellPadding: 1.5, overflow: 'linebreak', styles: { fontSize: 7 } } // Lien - très large avec retour à la ligne
        },
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: 'linebreak'
        },
        didDrawCell: function (data) {
          // Ajouter les images dans la colonne "Image" (index 1)
          if (data.column.index === 1 && data.row.index > 0 && data.row.index <= items.length) {
            const rowIndex = data.row.index - 1 // -1 car row 0 est l'en-tête
            const imageResult = imageResultsMap.get(rowIndex)
            
            if (imageResult && imageResult.imageDataUrl) {
              try {
                const cell = data.cell
                const imgSize = Math.min(cell.width - 2, cell.height - 2, 15) // Taille max 15mm
                const imgX = cell.x + (cell.width - imgSize) / 2
                const imgY = cell.y + (cell.height - imgSize) / 2
                
                doc.addImage(
                  imageResult.imageDataUrl,
                  imageResult.imageFormat,
                  imgX,
                  imgY,
                  imgSize,
                  imgSize
                )
              } catch (imgError) {
                console.error(`Erreur ajout image ligne ${rowIndex}:`, imgError)
              }
            }
          }
          
          // Ajouter les liens interactifs dans la colonne "Lien" (index 8 maintenant)
          if (data.column.index === 8 && data.row.index > 0 && data.row.index <= items.length) {
            const rowIndex = data.row.index - 1
            const item = items[rowIndex]
            const link = item.link || ''
            
            if (link) {
              const cell = data.cell
              try {
                doc.link(cell.x, cell.y, cell.width, cell.height, {
                  url: link
                })
              } catch (err) {
                console.error('Erreur ajout lien:', err)
              }
            }
          }
        },
        didParseCell: function (data) {
          // Après le parsing, styliser les liens et forcer le retour à la ligne
          if (data.column.index === 8 && data.row.index > 0 && data.row.index <= items.length) {
            const rowIndex = data.row.index - 1
            const item = items[rowIndex]
            if (item.link) {
              data.cell.styles.textColor = [0, 0, 255]
              data.cell.styles.fontStyle = 'underline'
              // Forcer le retour à la ligne pour les longs liens
              data.cell.styles.overflow = 'linebreak'
              data.cell.styles.cellWidth = 100 // Largeur explicite pour la colonne lien
              // Taille de police normale pour les liens
              data.cell.styles.fontSize = 7
            }
          }
        }
      })


      // Ajouter le total après le tableau
      const finalY = (doc.lastAutoTable?.finalY || 200) + 10
      
      // Vérifier si on a besoin d'une nouvelle page pour le total
      // En format paysage, la hauteur est de 210mm (au lieu de 297mm en portrait)
      let totalY = finalY
      if (totalY > 190) {
        doc.addPage()
        totalY = 20
      }
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...textColor)
      doc.text(`Total du panier: ${totalPanier.toFixed(2)} €`, 14, totalY)
      
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Nombre de produits: ${items.length}`, 14, totalY + 7)

      // Footer avec numéro de page
      const pageCount = doc.internal.pages.length - 1
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.text(
          `Page ${i} sur ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        )
      }

      // Sauvegarder le PDF
      const fileName = `${currentCart.name.replace(/[^a-z0-9]/gi, '_')}_export.pdf`
      doc.save(fileName)

      notify.success('Export PDF réussi !')
    } catch (error) {
      console.error('Erreur export PDF:', error)
      notify.error('Erreur lors de l\'export PDF: ' + error.message)
    }
  }

  // Export HTML avec images et liens interactifs
  const handleExportHTML = async () => {
    if (!currentCart || items.length === 0) {
      notify.warning('Le panier est vide ou introuvable')
      return
    }

    try {
      // Télécharger les images et les convertir en base64
      const imagePromises = items.map(async (item, index) => {
        if (item.image_url) {
          try {
            const blob = await downloadImageAsBlob(item.image_url)
            if (blob) {
              const reader = new FileReader()
              const imageDataUrl = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(blob)
              })
              return { index, imageDataUrl }
            }
          } catch (error) {
            console.error(`Erreur téléchargement image ${item.name}:`, error)
          }
        }
        return { index, imageDataUrl: null }
      })
      
      const imageResults = await Promise.all(imagePromises)
      const imageMap = new Map()
      imageResults.forEach(result => {
        if (result.imageDataUrl) {
          imageMap.set(result.index, result.imageDataUrl)
        }
      })

      const htmlContent = buildExportedCartHtml({
        cart: currentCart,
        items,
        imageMap,
      })

      // Créer un blob et télécharger le fichier HTML
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentCart.name.replace(/[^a-z0-9]/gi, '_')}_export.html`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      notify.success('Export HTML réussi !')
    } catch (error) {
      console.error('Erreur export HTML:', error)
      notify.error('Erreur lors de l\'export HTML: ' + error.message)
    }
  }

  // Import depuis un fichier
  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.zip,.json,.csv,.html'
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      try {
        if (file.name.endsWith('.zip')) {
          // Import depuis ZIP
          const zip = new JSZip()
          const zipData = await zip.loadAsync(file)
          
          // Chercher le fichier JSON
          let cartData = null
          for (const filename of Object.keys(zipData.files)) {
            if (filename.endsWith('.json') && !filename.includes('info')) {
              const content = await zipData.files[filename].async('string')
              cartData = JSON.parse(content)
              break
            }
          }

          if (!cartData || !cartData.items) {
            notify.error('Format de fichier invalide. Le fichier JSON est introuvable.')
            return
          }

          // Vider le panier actuel (optionnel, ou demander confirmation)
          if (items.length > 0) {
            const confirmed = await notify.confirm(`Ceci va remplacer ${items.length} produit(s) existant(s). Continuer ?`)
            if (!confirmed) {
              return
            }
            // Supprimer tous les items existants
            for (const item of items) {
              await itemsService.delete(item.id)
            }
            queryClient.invalidateQueries(['items', cartId])
          }

          // Uploader les images et créer les items
          const imagesFolder = zipData.folder('images')

          for (const itemData of cartData.items) {
            let image_url = ''
            let image_key = ''

            // Si l'item a une image, la télécharger depuis le ZIP
            if (itemData.image_filename) {
              try {
                const imageFile = imagesFolder.file(itemData.image_filename)
                if (imageFile) {
                  const imageBlob = await imageFile.async('blob')
                  const imageFileObj = new File([imageBlob], itemData.image_filename, { type: imageBlob.type })
                  
                  const formData = new FormData()
                  formData.append('file', imageFileObj)
                  
                  const response = await fetch(apiUrl('/api/upload/image'), {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    },
                    body: formData
                  })

                  if (response.ok) {
                    const result = await response.json()
                    image_url = result.url
                    image_key = result.key
                  }
                }
              } catch (err) {
                console.error('Erreur upload image:', err)
              }
            }

            // Créer l'item
            await itemsService.create(cartId, {
              name: itemData.name,
              link: itemData.link || '',
              price: itemData.price || 0,
              quantity: itemData.quantity || 1,
              promo: itemData.promo || '',
              certainty_label: itemData.certainty_label || 'Faible',
              image_url: image_url,
              image_key: image_key
            })
          }
          
          queryClient.invalidateQueries(['items', cartId])
          notify.success(`Import réussi ! ${cartData.items.length} produit(s) importé(s).`)
        } else if (file.name.endsWith('.json')) {
          // Import depuis JSON simple
          const text = await file.text()
          const cartData = JSON.parse(text)

          if (!cartData.items) {
            notify.error('Format de fichier invalide.')
            return
          }

          // Vider le panier actuel
          if (items.length > 0) {
            const confirmed = await notify.confirm(`Ceci va remplacer ${items.length} produit(s) existant(s). Continuer ?`)
            if (!confirmed) {
              return
            }
            for (const item of items) {
              await itemsService.delete(item.id)
            }
            queryClient.invalidateQueries(['items', cartId])
          }

          // Créer les items (sans images si pas de ZIP)
          for (const itemData of cartData.items) {
            await itemsService.create(cartId, {
              name: itemData.name,
              link: itemData.link || '',
              price: itemData.price || 0,
              quantity: itemData.quantity || 1,
              promo: itemData.promo || '',
              certainty_label: itemData.certainty_label || 'Faible',
              image_url: '',
              image_key: ''
            })
          }
          
          queryClient.invalidateQueries(['items', cartId])
          notify.success(`Import réussi ! ${cartData.items.length} produit(s) importé(s).\nNote: Les images ne peuvent être restaurées qu'avec un fichier ZIP.`)
        } else if (file.name.endsWith('.csv')) {
          // Import depuis CSV
          const text = await file.text()
          
          // Parser le CSV (supporte le point-virgule et la virgule comme délimiteur)
          const lines = text.split('\n').filter(line => line.trim())
          
          // Trouver la ligne de début des données (après les en-têtes)
          let dataStartIndex = -1
          for (let i = 0; i < lines.length; i++) {
            if (lines[i].includes('N°') || lines[i].toLowerCase().includes('nom')) {
              dataStartIndex = i + 1
              break
            }
          }
          
          if (dataStartIndex === -1) {
            notify.error('Format CSV invalide. Impossible de trouver les en-têtes.')
            return
          }
          
          // Détecter le délimiteur (point-virgule ou virgule)
          const delimiter = lines[dataStartIndex - 1].includes(';') ? ';' : ','
          
          // Extraire les données
          const itemsToImport = []
          for (let i = dataStartIndex; i < lines.length; i++) {
            const line = lines[i].trim()
            
            // Ignorer les lignes vides ou les lignes de total
            if (!line || line.toLowerCase().includes('total') || line.toLowerCase().includes('nombre')) {
              continue
            }
            
            // Parser la ligne CSV (gérer les guillemets)
            const values = []
            let currentValue = ''
            let inQuotes = false
            
            for (let j = 0; j < line.length; j++) {
              const char = line[j]
              
              if (char === '"') {
                inQuotes = !inQuotes
              } else if ((char === delimiter || char === ';') && !inQuotes) {
                values.push(currentValue.trim())
                currentValue = ''
              } else {
                currentValue += char
              }
            }
            values.push(currentValue.trim()) // Ajouter la dernière valeur
            
            // Structure attendue: N°, Nom, Prix unitaire, Quantité, Prix total, Promotion, Certitude, Lien, [Image]
            if (values.length >= 8) {
              const name = values[1]?.replace(/"/g, '') || ''
              const priceStr = values[2]?.replace(/[€,\s]/g, '').replace(',', '.') || '0'
              const quantityStr = values[3] || '1'
              const promo = values[5]?.replace(/"/g, '') || ''
              const certainty = values[6]?.replace(/"/g, '') || 'Faible'
              const link = values[7]?.replace(/"/g, '') || ''
              
              // Ignorer si le nom est vide
              if (!name || name === 'Nom du produit') {
                continue
              }
              
              itemsToImport.push({
                name: name,
                link: link,
                price: parseFloat(priceStr) || 0,
                quantity: parseInt(quantityStr) || 1,
                promo: promo,
                certainty_label: certainty || 'Faible',
                image_url: '',
                image_key: ''
              })
            }
          }
          
          if (itemsToImport.length === 0) {
            notify.warning('Aucun produit trouvé dans le fichier CSV.')
            return
          }
          
          // Vider le panier actuel
          if (items.length > 0) {
            const confirmed = await notify.confirm(`Ceci va remplacer ${items.length} produit(s) existant(s) par ${itemsToImport.length} produit(s). Continuer ?`)
            if (!confirmed) {
              return
            }
            for (const item of items) {
              await itemsService.delete(item.id)
            }
            queryClient.invalidateQueries(['items', cartId])
          }
          
          // Créer les items
          for (const itemData of itemsToImport) {
            await itemsService.create(cartId, itemData)
          }
          
          queryClient.invalidateQueries(['items', cartId])
          notify.success(`Import CSV réussi ! ${itemsToImport.length} produit(s) importé(s).\nNote: Les images ne peuvent être restaurées qu'avec un fichier ZIP.`)
        } else if (file.name.endsWith('.html')) {
          // Import depuis HTML
          const text = await file.text()
          
          // Créer un élément DOM temporaire pour parser le HTML
          const parser = new DOMParser()
          const doc = parser.parseFromString(text, 'text/html')
          
          // Trouver le tableau
          const table = doc.querySelector('table')
          if (!table) {
            notify.error('Format HTML invalide. Aucun tableau trouvé.')
            return
          }
          
          // Extraire les lignes du tableau (ignorer l'en-tête)
          const rows = Array.from(table.querySelectorAll('tbody tr'))
          const itemsToImport = []
          
          rows.forEach((row, index) => {
            const cells = Array.from(row.querySelectorAll('td'))
            
            if (cells.length >= 8) {
              // Structure attendue: N°, Image, Nom, Prix unitaire, Quantité, Prix total, Promotion, Certitude, Lien
              const name = cells[2]?.textContent?.trim() || ''
              const priceStr = cells[3]?.textContent?.replace(/[€,\s]/g, '').replace(',', '.') || '0'
              const quantityStr = cells[4]?.textContent?.trim() || '1'
              const promo = cells[6]?.textContent?.trim() || ''
              const certainty = cells[7]?.textContent?.trim() || 'Faible'
              
              // Extraire le lien (peut être dans un <a> ou directement dans la cellule)
              let link = ''
              const linkElement = cells[8]?.querySelector('a')
              if (linkElement) {
                link = linkElement.href || linkElement.textContent?.trim() || ''
              } else {
                link = cells[8]?.textContent?.trim() || ''
              }
              
              // Ignorer si le nom est vide
              if (!name || name === 'Nom du produit') {
                return
              }
              
              itemsToImport.push({
                name: name,
                link: link,
                price: parseFloat(priceStr) || 0,
                quantity: parseInt(quantityStr) || 1,
                promo: promo,
                certainty_label: certainty || 'Faible',
                image_url: '',
                image_key: ''
              })
            }
          })
          
          if (itemsToImport.length === 0) {
            notify.warning('Aucun produit trouvé dans le fichier HTML.')
            return
          }
          
          // Vider le panier actuel
          if (items.length > 0) {
            const confirmed = await notify.confirm(`Ceci va remplacer ${items.length} produit(s) existant(s) par ${itemsToImport.length} produit(s). Continuer ?`)
            if (!confirmed) {
              return
            }
            for (const item of items) {
              await itemsService.delete(item.id)
            }
            queryClient.invalidateQueries(['items', cartId])
          }
          
          // Créer les items
          for (const itemData of itemsToImport) {
            await itemsService.create(cartId, itemData)
          }
          
          queryClient.invalidateQueries(['items', cartId])
          notify.success(`Import HTML réussi ! ${itemsToImport.length} produit(s) importé(s).\nNote: Les images ne peuvent être restaurées qu'avec un fichier ZIP.`)
        }
      } catch (error) {
        console.error('Erreur import:', error)
        notify.error('Erreur lors de l\'import: ' + error.message)
      }
    }
    input.click()
  }

  // Utiliser les traductions pour les niveaux de certitude (pour l'affichage)
  const CERT_LEVELS = [t('veryLow'), t('low'), t('medium'), t('high'), t('veryHigh')]
  // Valeurs réelles stockées dans la base de données
  const CERT_LEVELS_VALUES = ['Très faible', 'Faible', 'Moyenne', 'Élevée', 'Quasi certain']
  
  // Handlers pour la recherche sémantique
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    setSemanticSearchTerm(value)
    setShowSemanticDropdown(value.trim().length > 0)
  }

  const handleSearchFocus = () => {
    setSearchExpanded(true)
    if (semanticSearchTerm.trim().length > 0) {
      setShowSemanticDropdown(true)
    }
  }

  const handleSearchBlur = (e) => {
    if (e.relatedTarget?.closest?.('[data-testid="semantic-search-panel"]')) {
      return
    }
    setSearchExpanded(false)
  }

  const closeSemanticPanel = () => {
    setShowSemanticDropdown(false)
  }

  const handleOpenSearchResult = (item) => {
    if (!item) return
    setShowSemanticDropdown(false)
    setSearchExpanded(false)
    if (item.cart_id && item.cart_id === cartId) {
      setHighlightedItemId(item.id)
      const node = document.querySelector(`[data-item-id="${CSS.escape(String(item.id))}"]`)
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (item.cart_id) {
      navigate(`/carts/${item.cart_id}`, { state: { highlightItemId: item.id } })
    }
  }

  const handleGridMenuAction = (actionId, item) => {
    if (!item) return
    if (actionId === 'history') {
      historyTriggerRef.current = gridMenuAnchorRef.current
      setSelectedItemForHistory({
        id: item.id,
        name: item.name,
        image_url: item.image_url,
      })
      setShowPriceHistoryModal(true)
      return
    }
    if (actionId === 'refresh') {
      if (isItemRefreshing(refreshingItemIds, item.id)) return
      refreshPriceMutation.mutate(item.id)
      return
    }
    if (actionId === 'open') {
      const safeUrl = sanitizeUrl(item.link)
      if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer')
      return
    }
    if (actionId === 'edit') {
      handleEditItem(item)
      return
    }
    if (actionId === 'saved') {
      moveToSavedMutation.mutate(item.id)
      return
    }
    if (actionId === 'delete') {
      setDeleteError('')
      setItemToDelete(item)
    }
  }

  useEffect(() => {
    const highlightId = location.state?.highlightItemId
    if (!highlightId) return
    setHighlightedItemId(highlightId)
    const frame = window.requestAnimationFrame(() => {
      const node = document.querySelector(`[data-item-id="${CSS.escape(String(highlightId))}"]`)
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    navigate(location.pathname, { replace: true, state: {} })
    return () => window.cancelAnimationFrame(frame)
  }, [location.state?.highlightItemId, location.pathname, navigate])

  useEffect(() => {
    if (!highlightedItemId) return undefined
    const timer = window.setTimeout(() => setHighlightedItemId(null), 2800)
    return () => window.clearTimeout(timer)
  }, [highlightedItemId])

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price || 0)
  }

  return (
    <div
      className={`cart-detail-page${showLibraryModal && libraryPlacement.mode === 'reflow' ? ' is-library-reflow' : ''}`}
      style={showLibraryModal && libraryPlacement.mode === 'reflow' ? {
        '--library-workspace-pad': `${libraryPlacement.workspacePadRight}px`,
      } : undefined}
    >
      <svg style={{ display: 'none' }}>
        <filter id="lg-dist" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.008 0.008" numOctaves="2" seed="92" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
          <feDisplacementMap in="SourceGraphic" in2="blurred" scale="70" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id="glass-distortion">
          <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
        </filter>
      </svg>

      <div className="cart-detail-header">
        {isPhoneLayout ? (
          <HeaderCommandBar>
            <button className="btn-icon header-cmd-home" onClick={goBackFromCart} title={fromAnalytics ? 'Retour aux statistiques' : 'Retour aux paniers'}>
              <img src="/Icon house.png" alt={fromAnalytics ? 'Retour aux statistiques' : 'Retour aux paniers'} />
            </button>
            <button className="btn-icon header-cmd-library cart-action-library" onClick={() => setShowLibraryModal(true)} title={t('library')}>
              <img src="/Icon book.png" alt={t('library')} />
            </button>
            <div className="header-cmd-theme">
              <ThemeSwitcher variant="compact" />
            </div>
            <div className="header-cmd-bell">
              <NotificationBell />
            </div>
            <button ref={toolsButtonRef} className="btn-icon header-cmd-tools" onClick={() => setShowToolsMenu(!showToolsMenu)} title={t('tools')}>
              <img src="/Icon construction.png" alt={t('tools')} />
            </button>
            <button
              ref={addProductButtonRef}
              className="btn-icon header-cmd-add cart-action-add"
              onClick={() => {
                setAddProductError('')
                setShowAddChooser(true)
              }}
              title={t('addProduct')}
            >
              <img src="/Icon add circle outline.png" alt={t('add')} />
            </button>
          </HeaderCommandBar>
        ) : (
          <div className="header-nav">
            <button className="btn-icon" onClick={goBackFromCart} title={fromAnalytics ? 'Retour aux statistiques' : 'Retour aux paniers'}>
              <img src="/Icon house.png" alt={fromAnalytics ? 'Retour aux statistiques' : 'Retour aux paniers'} />
            </button>
            <LanguageSelector position="left" />
            <ThemeSwitcher />
          </div>
        )}
        <h1 className="cart-name">{cartUnavailable ? 'Panier indisponible' : (currentCart?.name || t('home'))}</h1>
        <div className="header-actions">
          <div className="cart-search-slot">
            <div className={`search-input-wrapper${searchExpanded ? ' is-expanded' : ''}`} ref={searchInputRef}>
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder={t('search')}
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                className="search-input"
                aria-expanded={searchExpanded}
                aria-controls="semantic-search-panel"
                onPointerDown={() => setSearchExpanded(true)}
              />
              <SemanticSearchPanel
                open={showSemanticDropdown && semanticQuery.length > 0}
                anchorRef={searchInputRef}
                query={semanticQuery}
                isLoading={isLoadingSemantic}
                isFetching={isFetchingSemantic}
                isError={isSemanticError}
                error={semanticError}
                items={semanticItems}
                savedItems={semanticSavedItems}
                currentCartId={cartId}
                formatPrice={formatPrice}
                onClose={closeSemanticPanel}
                onSelect={handleOpenSearchResult}
                onImageError={handleItemImageError}
              />
            </div>
          </div>
          {isPhoneLayout ? null : (
            <div className="cart-header-toolbar">
              <button ref={toolsButtonRef} className="btn-icon" onClick={() => setShowToolsMenu(!showToolsMenu)} title={t('tools')}>
                <img src="/Icon construction.png" alt={t('tools')} />
              </button>
              <NotificationBell />
              <button className="btn-icon cart-action-library" onClick={() => setShowLibraryModal(true)} title={t('library')}>
                <img src="/Icon book.png" alt={t('library')} />
              </button>
              <button
                ref={addProductButtonRef}
                className="btn-icon cart-action-add"
                onClick={() => {
                  setAddProductError('')
                  setShowAddModal(true)
                }}
                title={t('addProduct')}
              >
                <img src="/Icon add circle outline.png" alt={t('add')} />
              </button>
            </div>
          )}
        </div>
      </div>

      <CartFiltersPanel
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        anchorRef={advancedFiltersButtonRef}
        filters={cartFilters}
        onChange={(next) => setCartFilters(sanitizeCartFilters(next))}
        onReset={resetFilters}
        t={t}
      />

      <div className="cart-filters">
        <div style={{ position: 'relative' }}>
          <label 
            ref={advancedFiltersButtonRef}
            className={`hamburger-filter${activeFilterCount ? ' has-active' : ''}`}
            title={t('advancedFilters')}
          >
            <input 
              type="checkbox"
              checked={showAdvancedFilters}
              onChange={(e) => setShowAdvancedFilters(e.target.checked)}
            />
            <svg viewBox="0 0 32 32">
              <path className="line line-top-bottom" d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"></path>
              <path className="line" d="M7 16 27 16"></path>
            </svg>
            {activeFilterCount > 0 ? (
              <span className="hamburger-filter-count" aria-label={`${activeFilterCount} filtres actifs`}>
                {activeFilterCount}
              </span>
            ) : null}
          </label>
          <span>Prix:</span>
          <button
            onClick={() => setCartPriceSort(cartPriceSort === 'asc' ? 'desc' : cartPriceSort === 'desc' ? null : 'asc')}
            className="btn-icon-small"
            style={{
              background: cartPriceSort ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            }}
            title={cartPriceSort === 'asc' ? 'Croissant' : cartPriceSort === 'desc' ? 'Décroissant' : 'Tri par prix'}
          >
            <img 
              src={cartPriceSort === 'desc' ? "/Icon south.png" : "/Icon north.png"} 
              alt={cartPriceSort === 'desc' ? "Décroissant" : "Croissant"}
              style={{ 
                width: '18px', 
                height: '18px',
                opacity: cartPriceSort ? 1 : 0.5
              }} 
            />
          </button>
        </div>
        <div className="view-mode-toggle">
          <button
            className="btn-icon-small"
            onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            title={viewMode === 'list' ? t('gridView') : t('listView')}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 8px',
              transition: 'all 0.3s ease',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px'
            }}
          >
            <img 
              src={viewMode === 'list' ? "/Icon grid view.png" : "/Icon view list.png"} 
              alt={viewMode === 'list' ? t('gridView') : t('listView')} 
              style={{ width: '20px', height: '20px', opacity: 0.8 }} 
            />
          </button>
        </div>
      </div>

      <div className="cart-detail-container" ref={cardsAnchorRef}>
        <EditProductModal
          isOpen={showEditModal}
          item={editingItem}
          onClose={() => {
            if (!editSaving) {
              setShowEditModal(false)
              setEditProductError('')
            }
          }}
          onSubmit={(data) => {
            if (editSaving || !editingItem?.id) return
            setEditProductError('')
            updateMutation.mutate({
              itemId: editingItem.id,
              data,
              source: 'edit-modal',
              previousPrice: editSnapshotRef.current?.id === editingItem.id
                ? editSnapshotRef.current.price
                : Number(editingItem.price) || 0,
            })
          }}
          isSubmitting={editSaving}
          submitError={editProductError}
          returnFocusRef={editTriggerRef}
          t={t}
        />

        {isPhoneLayout ? (
          <AddProductChooser
            isOpen={showAddChooser}
            onClose={() => setShowAddChooser(false)}
            onManual={() => {
              setShowAddChooser(false)
              setAddProductError('')
              setShowAddModal(true)
            }}
            onScan={() => {
              setShowAddChooser(false)
              setAddProductError('')
              setShowScanFlow(true)
            }}
            returnFocusRef={addProductButtonRef}
          />
        ) : null}
        {isPhoneLayout ? (
          <ScanProductFlow
            isOpen={showScanFlow}
            onClose={() => {
              setShowScanFlow(false)
              setShowAddChooser(true)
            }}
            onManual={() => {
              setShowScanFlow(false)
              setShowAddChooser(false)
              setShowAddModal(true)
            }}
            onConfirm={async (item) => {
              if (createMutation.isPending) return
              const clientMutationId = item.client_mutation_id || createClientMutationId()
              const payload = { ...item, client_mutation_id: clientMutationId }
              setAddProductError('')
              await scanOutbox.enqueue({
                clientMutationId,
                cartId,
                draftPayload: payload,
                status: 'syncing',
              })
              queryClient.setQueryData(itemsCacheKey, (previous) => mergeCreatedItem(previous, {
                ...payload,
                id: `pending-${clientMutationId}`,
                cart_id: cartId,
                sync_pending: true,
                sync_status: 'syncing',
              }))
              createMutation.mutate(payload)
            }}
            onQueueOffline={async ({
              payload,
              originalPhoto,
              additionalPhotos,
              conversionPending,
              clientMutationId: queuedMutationId,
            }) => {
              const clientMutationId = queuedMutationId || payload.client_mutation_id || createClientMutationId()
              const queuedPayload = {
                ...payload,
                client_mutation_id: clientMutationId,
              }
              await scanOutbox.enqueue({
                clientMutationId,
                cartId,
                draftPayload: queuedPayload,
                captureBlob: originalPhoto,
                additionalBlobs: additionalPhotos,
                conversionPending,
                status: 'pending',
              })
              queryClient.setQueryData(itemsCacheKey, (previous) => mergeCreatedItem(previous, {
                ...queuedPayload,
                id: `pending-${clientMutationId}`,
                cart_id: cartId,
                sync_pending: true,
                sync_status: 'pending',
              }))
              setShowScanFlow(false)
            }}
            submitting={createMutation.isPending}
            submitError={addProductError}
            returnFocusRef={addProductButtonRef}
          />
        ) : null}
        {isPhoneLayout ? (
          <ProductPlaceSheet
            item={placeItem}
            isOpen={Boolean(placeItem)}
            onClose={() => setPlaceItem(null)}
            onUpdate={(store) => {
              if (!placeItem?.id) return
              const nextMeta = applyStoreToMeta({ ...(placeItem.item_meta || {}) }, store)
              updateMutation.mutate({
                itemId: placeItem.id,
                data: { item_meta: nextMeta },
                source: 'scan-place',
              })
              setPlaceItem(null)
            }}
            returnFocusRef={addProductButtonRef}
          />
        ) : null}

        <AddProductModal
          isOpen={showAddModal}
          onClose={() => {
            if (!createMutation.isPending) setShowAddModal(false)
          }}
          onSubmit={(item) => {
            if (createMutation.isPending) return
            setAddProductError('')
            createMutation.mutate(item)
          }}
          onExtract={handleExtractProduct}
          onScanPhoto={isPhoneLayout ? () => {
            setShowAddModal(false)
            setAddProductError('')
            setShowScanFlow(true)
          } : undefined}
          isSubmitting={createMutation.isPending}
          submitError={addProductError}
          returnFocusRef={addProductButtonRef}
          t={t}
        />
        <AddProductSuccess
          payload={addSuccessPayload}
          onDone={clearAddSuccess}
        />

        <div className="cart-summary glass-container">
          <div className="glass-filter"></div>
          <div className="glass-overlay"></div>
          <div className="glass-specular"></div>
          <div className="glass-content">
          <div className="summary-metrics">
          <div className="summary-item summary-item-count">
              <span className="summary-text">
                {t('articles')} : {itemsLoaded ? items.length : '—'}
              </span>
              <span className="summary-label">{t('articles')}</span>
              <span className="summary-value">{itemsLoaded ? items.length : '—'}</span>
          </div>
          <div className="summary-item summary-item-total">
              <span className="summary-text">
                {t('total')} : {itemsLoaded
                  ? (mobileJpyTotal > 0 ? formatJpy(mobileJpyTotal) : `€ ${total.toFixed(2)}`)
                  : '—'}
              </span>
              <span className="summary-label">{t('total')}</span>
              <span className="summary-value">
                {itemsLoaded
                  ? (mobileJpyTotal > 0 ? formatJpy(mobileJpyTotal) : formatPrice(total))
                  : '—'}
              </span>
              {itemsLoaded && mobileJpyTotal > 0 && total > 0 ? (
                <span className="summary-secondary">{formatPrice(total)}</span>
              ) : null}
            </div>
          </div>
          <div className="summary-actions">
            {itemsLoaded && items.some(item => item.link) && (
              <>
                <button
                  ref={refreshAllButtonRef}
                  type="button"
                  className="btn-refresh-all-prices"
                  aria-haspopup="menu"
                  aria-expanded={contextMenu.isOpen && contextMenu.target === 'all'}
                  aria-label={refreshAllPricesMutation.isPending ? 'Mise à jour en cours' : 'Actualiser tous les prix'}
                  onClick={() => {
                    if (refreshAllMenu.consume()) return
                    if (!refreshAllPricesMutation.isPending) {
                      refreshAllPricesMutation.mutate()
                    }
                  }}
                  onPointerDown={refreshAllMenu.onPointerDown}
                  onPointerUp={refreshAllMenu.onPointerUp}
                  onPointerCancel={refreshAllMenu.onPointerCancel}
                  onPointerLeave={refreshAllMenu.onPointerLeave}
                  onContextMenu={refreshAllMenu.onContextMenu}
                  onKeyDown={refreshAllMenu.onKeyDown}
                  disabled={refreshAllPricesMutation.isPending}
                  title={refreshAllPricesMutation.isPending ? 'Mise à jour en cours...' : 'Actualiser tous les prix. Clic droit, appui long ou Maj+F10 pour la méthode.'}
                >
                  {refreshAllPricesMutation.isPending ? (
                    <span className="price-refresh-spinner" aria-hidden="true" />
                  ) : (
                    <img src="/Icon euro symbol.png" alt="" />
                  )}
                </button>
              </>
            )}
            {/* Bouton d'optimisation budgétaire - toujours visible si budget défini */}
            {itemsLoaded && currentCart?.budget != null && currentCart.budget !== '' && (
              <button
                className="btn-optimize-budget-compact"
                onClick={() => setShowOptimizationModal(true)}
                title="Système de redressement budgétaire"
              >
                <img src="/Icon brain.png" alt="Optimiser" />
              </button>
            )}
          </div>
            {(refreshProgress.isActive || refreshProgress.isExiting) && refreshProgress.total > 0 && (
              <div style={{ width: '100%', marginTop: '12px', flexBasis: '100%', paddingLeft: '1.5rem' }}>
                <LiquidGlassProgressBar
                  current={refreshProgress.current}
                  total={refreshProgress.total}
                  label="Actualisation des prix en cours..."
                  isExiting={refreshProgress.isExiting}
                />
              </div>
            )}
            {(() => {
              const model = budgetSummaryModel(total, currentCart?.budget)
              if (!itemsLoaded || !model.hasBudget) return null
              const percentLabel = `${Math.round(model.percent)} %`
              return (
                  <div className="summary-budget">
                    <div className="budget-info">
                    <span className="budget-label">{t('budget')} : € {model.budget.toFixed(2)}</span>
                      <span className="budget-percentage">
                      {percentLabel}
                      </span>
                    </div>
                  {model.over ? (
                      <div className="budget-adjustment">
                        <span className="budget-adjustment-label budget-over">
                        {t('budgetAdjustment')} : € {model.overAmount.toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <div className="budget-remaining">
                      <span className={`budget-remaining-label ${model.status === 'warning' ? 'budget-warning' : 'budget-ok'}`}>
                        {t('budgetRemaining')} : € {model.remaining.toFixed(2)}
                        </span>
                      </div>
                    )}
                  <div
                    className="budget-progress-container"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(model.fillPercent)}
                    aria-label={`${t('budget')} ${percentLabel}`}
                  >
                      <div 
                      className={`budget-progress-bar budget-${model.status}`}
                      style={{ width: `${model.fillPercent}%` }}
                      >
                        <div className="budget-progress-fill"></div>
                      </div>
                    </div>
                  </div>
              )
            })()}
          </div>
        </div>

        {itemsState === 'refresh-error' && (
          <div className="empty-state cart-products-refresh-error">
            <div className="empty-icon">⚠️</div>
            <h3>Échec de l’actualisation</h3>
            <p>Les derniers articles chargés restent affichés. {itemsErrorMessage}</p>
            {itemsStatus !== 403 && (
              <button className="btn-primary" onClick={() => refetchItems()}>
                Réessayer
              </button>
            )}
          </div>
        )}

        {cartUnavailable ? (
          <div className="empty-state">
            <div className="empty-icon">🚫</div>
            <h3>Panier indisponible</h3>
            <p>Ce panier a été supprimé ou n’est plus accessible.</p>
            <button className="btn-primary" type="button" onClick={goBackFromCart}>
              {fromAnalytics ? 'Retour aux statistiques' : 'Retour aux paniers'}
            </button>
          </div>
        ) : itemsState === 'loading' ? (
          <div className="cart-products-region is-loading" aria-busy="true">
            <PegTopLoader label="Chargement des produits…" />
          </div>
        ) : itemsState === 'error' || itemsState === 'forbidden' ? (
          <div className="cart-products-region is-error">
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <h3>{itemsState === 'forbidden' || itemsStatus === 403 ? 'Accès aux articles refusé' : 'Articles indisponibles'}</h3>
              <p>{itemsErrorMessage}</p>
              {itemsState !== 'forbidden' && itemsStatus !== 403 && (
                <button className="btn-primary" onClick={() => refetchItems()}>
                  Réessayer
                </button>
              )}
            </div>
          </div>
        ) : itemsState === 'empty' || (itemsState === 'refresh-error' && items.length === 0) ? (
          <div className="cart-products-region is-empty">
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>Aucun produit</h3>
              <p>Ajoutez votre premier produit au panier</p>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="cart-products-region is-filtered-empty">
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>Aucun résultat</h3>
              <p>Aucun produit ne correspond à la recherche ou aux filtres.</p>
              {hasActiveFilters ? (
                <button className="btn-primary" onClick={() => { resetFilters(); setSearchTerm('') }}>
                  {t('resetFilters') || 'Réinitialiser'}
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div
            className={`cart-products-region${itemsState === 'refreshing' ? ' is-refreshing' : ''}`}
            aria-busy={itemsState === 'refreshing' || undefined}
          >
            {itemsState === 'refreshing' ? (
              <div className="cart-products-refresh" role="status" aria-live="polite">
                Actualisation…
              </div>
            ) : null}
            <div className={viewMode === 'grid' ? 'items-grid' : 'items-list'}>
            {filteredItems.map(item => (
              <div 
                key={item.id} 
                className={`item-card glass-container ${selectionMode && selectedItems.has(item.id) ? 'selected' : ''} ${savingItemId === item.id ? 'saving-to-library' : ''} ${justAddedIds.has(item.id) ? 'item-card-enter' : ''} ${highlightedItemId === item.id ? 'is-highlighted' : ''}`}
                data-item-id={item.id}
                onDoubleClick={(e) => {
                  const target = e.target
                  if (target.closest('button, a, input, .item-menu-trigger, .item-buttons, .btn-icon-small')) {
                    return
                  }
                  if (!selectionMode && !selectionModeActiveRef.current && !longPressActivatedRef.current) {
                    handleEditItem(item)
                  }
                }}
                onPointerDown={(e) => handleItemPointerDown(item.id, e)}
                onPointerMove={handleItemPointerMove}
                onPointerUp={handleItemPointerUp}
                onPointerCancel={cancelItemLongPressGesture}
                onClick={(e) => {
                  // Ne pas gérer le clic si on clique sur un bouton, un input, une image, ou leurs enfants
                  const target = e.target
                  if (target.tagName === 'BUTTON' || 
                      target.tagName === 'INPUT' || 
                      target.tagName === 'IMG' ||
                      target.closest('button') || 
                      target.closest('input:not(:disabled)') ||
                      target.closest('.item-buttons') ||
                      target.closest('.btn-icon-small') ||
                      target.closest('.item-image')) {
                    return
                  }
                  
                  if (selectionMode) { 
                    e.preventDefault()
                    e.stopPropagation()
                    // Ne pas permettre de désélectionner si cet item vient d'être sélectionné par long press
                    // ET que c'est le seul élément sélectionné
                    if (recentlySelectedByLongPressRef.current.has(item.id) && selectedItems.size === 1 && selectedItems.has(item.id)) {
                      // Ne pas désélectionner l'élément qui vient juste d'être sélectionné par long press
                      return
                    }
                    toggleItemSelection(item.id)
                  } else if (selectionModeActiveRef.current || longPressActivatedRef.current) {
                    // Si le long press vient de s'activer ou si le mode sélection est actif,
                    // ne pas déclencher le clic normal
                    e.preventDefault()
                    e.stopPropagation()
                    longPressActivatedRef.current = false
                  }
                }}
              >
                <div className="glass-filter"></div>
                <div className="glass-overlay"></div>
                <div className="glass-specular"></div>
                
                {/* Animation de mise de côté */}
                {savingItemId === item.id && (
                  <div className="saving-overlay">
                    <div className="saving-icon">
                      <img src="/Icon book.png" alt="Bibliothèque" />
                    </div>
                    <span className="saving-text">{t('movingToLibrary') || 'Mise de côté...'}</span>
                  </div>
                )}
                
                <div className="glass-content">
                <div 
                  className="item-image" 
                  style={{ cursor: (resolveCardImage(item).url || resolveCardImage(item).key) ? 'pointer' : 'default' }}
                >
                  <ProductImage
                    item={item}
                    alt={item.name}
                    role="button"
                    tabIndex={0}
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (longPressActivatedRef.current) return
                      if (selectionMode || selectionModeActiveRef.current) {
                        if (selectionMode) toggleItemSelection(item.id)
                        return
                      }
                      handleImageClick(item)
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      e.stopPropagation()
                      handleImageClick(item)
                    }}
                  />
                </div>
                <div className="item-content">
                  <div className="item-heading">
                    <div
                      className="item-name"
                      title={item.name || ''}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (longPressActivatedRef.current) return
                        if (selectionMode || selectionModeActiveRef.current) {
                          if (selectionMode) toggleItemSelection(item.id)
                          return
                        }
                        handleEditItem(item)
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.preventDefault()
                        e.stopPropagation()
                        if (!selectionMode && !selectionModeActiveRef.current) {
                          handleEditItem(item)
                        }
                      }}
                    >
                      {item.name || t('productName') || 'Produit sans nom'}
                    </div>
                    {item.sync_status === 'failed' ? (
                      <div className="scan-item-save-error">
                        <p>Enregistrement impossible</p>
                        <div className="scan-item-sync-actions">
                          <button type="button" onClick={(event) => { event.stopPropagation(); retryScanItem(item) }}>Réessayer</button>
                          <button type="button" onClick={(event) => { event.stopPropagation(); handleEditItem(item) }}>Modifier</button>
                        </div>
                      </div>
                    ) : (item.sync_pending || item.sync_status === 'retry' || item.sync_status === 'pending' || item.sync_status === 'syncing') ? (
                      <p className="scan-item-sync">
                        <ReloadSyncGlyph />
                        Enregistrement en attente
                      </p>
                    ) : null}
                    {viewMode === 'list' ? (
                      <button
                        type="button"
                        className="item-menu-trigger"
                        aria-label="Actions du produit"
                        aria-expanded={openGridMenuId === item.id}
                        aria-haspopup="menu"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => openItemActionsMenu(e, item)}
                      >
                        <KebabGlyph />
                      </button>
                    ) : null}
                  </div>
                  {(() => {
                    const shownLink = displayItemLink(item.link)
                    const itemStore = isPhoneLayout ? readItemStore(item) : null
                    if (!shownLink && !itemStore) return null
                    const placeLabel = formatPlaceCardLabel(itemStore) || 'Position enregistrée'
                    return (
                      <div className="item-secondary">
                        {shownLink ? (
                          <div className="item-link" title={item.link}>{shownLink}</div>
                        ) : null}
                        {itemStore ? (
                          <button
                            type="button"
                            className="scan-item-place"
                            title={itemStore.address || placeLabel}
                            aria-label={itemStore.address || placeLabel}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation()
                              setPlaceItem(item)
                            }}
                          >
                            <IconPin />
                            <span>{placeLabel}</span>
                          </button>
                        ) : null}
                      </div>
                    )
                  })()}
                  <div className="item-badges" style={item.promo ? undefined : { display: 'none' }}>
                    {item.promo ? <span className="badge badge-promo">{item.promo}</span> : null}
                  </div>
                </div>
                <div className="item-actions">
                  <div className="item-price-qty">
                    <div className="item-price-block">
                      <span className="item-metric-label">Prix</span>
                      <div className="item-price-value price-input">
                        {isPhoneLayout && readObservedPricing(item)?.currency === 'JPY' ? (
                          <span className="scan-item-jpy-price">
                            {formatJpy(readObservedPricing(item).amount)}
                          </span>
                        ) : (
                          <RollingPrice
                            value={item.price || 0}
                            fromValue={priceRoll?.itemId === item.id ? priceRoll.from : null}
                            playKey={priceRoll?.itemId === item.id ? priceRoll.key : null}
                            onSettled={() => {
                              setPriceRoll((current) => (current?.itemId === item.id ? null : current))
                            }}
                          />
                        )}
                      </div>
                      {isPhoneLayout && readObservedPricing(item)?.currency === 'JPY' ? (
                        readObservedPricing(item).convertedEur > 0 ? (
                          <div className="scan-item-eur-price">{formatPrice(readObservedPricing(item).convertedEur)}</div>
                        ) : item.sync_pending ? null : (
                          <div className="scan-item-eur-price is-pending">Conversion en attente</div>
                        )
                      ) : null}
                      {formatLastPriceCheck(item.last_price_check) && (
                        <div
                          className="item-price-updated"
                          title={`Dernière mise à jour du prix : ${formatLastPriceCheck(item.last_price_check)}`}
                        >
                          {formatLastPriceCheck(item.last_price_check)}
                        </div>
                      )}
                      {isItemRefreshing(refreshingItemIds, item.id) && (
                        <div className="item-price-updating">Actualisation…</div>
                      )}
                    </div>
                    <div className="item-qty-block">
                      <span className="item-metric-label">Qté</span>
                      <span className="item-qty-prefix">Qté</span>
                      <div className="item-qty-value qty-input">{item.quantity || 1}</div>
                    </div>
                  </div>
                  {viewMode === 'grid' ? (
                    <div className="item-buttons grid-actions-menu">
                      <button
                        type="button"
                        className="btn-icon-small grid-menu-trigger item-menu-trigger grid-hamburger-container"
                        aria-label="Actions du produit"
                        aria-expanded={openGridMenuId === item.id}
                        aria-haspopup="menu"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => openItemActionsMenu(e, item)}
                      >
                        <span
                          className={`grid-hamburger-label${openGridMenuId === item.id ? ` is-open is-dir-${gridMenuArrow}` : ''}`}
                          aria-hidden="true"
                        >
                          <span className="line1" />
                          <span className="line2" />
                          <span className="line3" />
                        </span>
                        <KebabGlyph />
                      </button>
                    </div>
                  ) : (
                    <div className="item-buttons item-buttons-direct">
                      <button
                        className="btn-icon-small"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (selectionMode || selectionModeActiveRef.current) {
                            e.preventDefault()
                            if (selectionMode) toggleItemSelection(item.id)
                          } else {
                            historyTriggerRef.current = e.currentTarget
                            setSelectedItemForHistory({
                              id: item.id,
                              name: item.name,
                              image_url: item.image_url,
                            })
                            setShowPriceHistoryModal(true)
                          }
                        }}
                        title="Voir l'historique des prix"
                      >
                        <img src="/Icon query stats.png" alt="Historique" />
                      </button>
                      {item.link && (
                        <button
                          className={`btn-icon-small${isItemRefreshing(refreshingItemIds, item.id) ? ' is-refreshing' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (selectionMode || selectionModeActiveRef.current) {
                              e.preventDefault()
                              if (selectionMode) toggleItemSelection(item.id)
                            } else if (!isItemRefreshing(refreshingItemIds, item.id)) {
                              refreshPriceMutation.mutate(item.id)
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setContextMenu({
                              isOpen: true,
                              position: { x: e.clientX, y: e.clientY },
                              target: 'single'
                            })
                          }}
                          disabled={isItemRefreshing(refreshingItemIds, item.id)}
                          title={isItemRefreshing(refreshingItemIds, item.id)
                            ? 'Actualisation en cours…'
                            : 'Actualiser le prix (clic droit pour choisir la méthode)'}
                        >
                          {isItemRefreshing(refreshingItemIds, item.id) ? (
                            <span className="price-refresh-spinner" aria-hidden="true" />
                          ) : (
                            <img src="/Icon euro symbol.png" alt="Actualiser" />
                          )}
                        </button>
                      )}
                      <button
                        className="btn-icon-small"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (selectionMode || selectionModeActiveRef.current) {
                            e.preventDefault()
                            if (selectionMode) toggleItemSelection(item.id)
                          } else if (item.link) {
                            const safeUrl = sanitizeUrl(item.link)
                            if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer')
                          }
                        }}
                        title="Ouvrir le lien"
                      >
                        <img src="/Icon chrome.png" alt="Ouvrir" />
                      </button>
                      <button
                        className="btn-icon-small"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (selectionMode || selectionModeActiveRef.current) {
                            e.preventDefault()
                            if (selectionMode) toggleItemSelection(item.id)
                          } else {
                            moveToSavedMutation.mutate(item.id)
                          }
                        }}
                        title="Mettre de côté"
                      >
                        <img src="/Icon inventory.png" alt="Mettre de côté" />
                      </button>
                      <button
                        className="btn-icon-small"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (selectionMode || selectionModeActiveRef.current) {
                            e.preventDefault()
                            if (selectionMode) toggleItemSelection(item.id)
                          } else {
                            setDeleteError('')
                            setItemToDelete(item)
                          }
                        }}
                        title="Supprimer"
                      >
                        <img src="/Icon trash.png" alt="Supprimer" />
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => {
          if (confirmModal.onCancel) confirmModal.onCancel()
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        }}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
      <ConfirmModal
        isOpen={!!itemToDelete}
        variant="delete"
        title="Supprimer ce produit ?"
        subject={itemToDelete?.name || ''}
        description="Le produit sera retiré de ce panier. Cette action ne peut pas être annulée."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        busy={deleteMutation.isPending}
        error={deleteError}
        onClose={() => {
          if (deleteMutation.isPending) return
          setItemToDelete(null)
          setDeleteError('')
        }}
        onConfirm={() => {
          if (!itemToDelete?.id || deleteMutation.isPending) return
          deleteMutation.mutate(itemToDelete.id)
        }}
      />

      <PriceHistoryModal
        isOpen={showPriceHistoryModal}
        onClose={() => setShowPriceHistoryModal(false)}
        itemId={selectedItemForHistory?.id}
        itemName={selectedItemForHistory?.name}
        itemImage={selectedItemForHistory?.image_url}
        returnFocusRef={historyTriggerRef}
      />

      <ToolsMenu
        isOpen={showToolsMenu}
        onClose={() => setShowToolsMenu(false)}
        buttonRef={toolsButtonRef}
        onExportJSON={handleExportJSON}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        onExportHTML={handleExportHTML}
        onImport={handleImport}
        onPaste={handlePaste}
        onSetBudget={currentCart ? handleOpenBudgetModal : null}
        onShare={() => setShowShareModal(true)}
        onCompare={() => setShowComparePicker(true)}
        hasBudget={currentCart && currentCart.budget && currentCart.budget > 0}
        showLanguage={isPhoneLayout}
        copiedItemsCount={(() => {
          // Toujours vérifier localStorage pour avoir la valeur à jour même après navigation
          try {
            const saved = localStorage.getItem('copiedItems')
            if (saved) {
              const parsed = JSON.parse(saved)
              return parsed.length
            }
          } catch (e) {
            // Ignorer les erreurs
          }
          return copiedItems?.length || 0
        })()}
      />

      <GridItemActionsMenu
        isOpen={Boolean(openGridMenuId)}
        anchor={gridMenuAnchorRef.current}
        item={items.find((entry) => entry.id === openGridMenuId) || gridMenuItemRef.current}
        refreshing={isItemRefreshing(refreshingItemIds, openGridMenuId)}
        labels={{
          history: t('priceHistory'),
          refresh: t('refreshPrice') || 'Actualiser le prix',
          refreshing: 'Actualisation…',
          open: t('openLink'),
          edit: t('edit'),
          saved: t('moveToSaved'),
          delete: t('delete'),
        }}
        onClose={() => setOpenGridMenuId(null)}
        onPlacementChange={(direction) => {
          if (direction) setGridMenuArrow(direction)
        }}
        onAction={handleGridMenuAction}
      />

      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => {
          if (updateCartMutation.isPending) return
          setShowBudgetModal(false)
          setBudgetValue('')
          updateCartMutation.reset()
        }}>
          <div className="modal glass-modal modal-budget" onClick={(e) => e.stopPropagation()}>
            <h2>{currentCart?.budget && currentCart.budget > 0 ? t('editBudget') : t('setBudget')}</h2>
            <form onSubmit={(e) => {
              e.preventDefault()
              if (updateCartMutation.isPending) return
              if (budgetValue.trim() && cartId) {
                const budget = parseFloat(budgetValue.trim())
                if (!isNaN(budget) && budget >= 0) {
                  updateCartMutation.mutate({ 
                    cartId, 
                    data: { name: currentCart?.name || '', budget: budget } 
                  })
                } else {
                  notify.warning('Veuillez entrer un budget valide')
                }
              } else {
                notify.warning('Veuillez entrer un budget')
              }
            }}>
              <div className="form-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t('budgetAmount')}
                  value={budgetValue}
                  onChange={(e) => {
                    setBudgetValue(e.target.value)
                    if (updateCartMutation.isError) updateCartMutation.reset()
                  }}
                  autoFocus
                  disabled={updateCartMutation.isPending}
                  aria-invalid={updateCartMutation.isError || undefined}
                />
              </div>
              {updateCartMutation.isError && (
                <p className="modal-budget-error" role="alert">
                  {updateCartMutation.error?.message || 'Impossible d’enregistrer le budget.'}
                </p>
              )}
              <div className="modal-actions">
                  <LiquidGlassButton
                    type="button"
                    className="modal-budget-icon-btn"
                    aria-label={t('cancel')}
                    title={t('cancel')}
                    disabled={updateCartMutation.isPending}
                    onClick={() => {
                      if (updateCartMutation.isPending) return
                      setShowBudgetModal(false)
                      setBudgetValue('')
                      updateCartMutation.reset()
                    }}
                  >
                    <BudgetCancelGlyph />
                  </LiquidGlassButton>
                  <LiquidGlassButton
                    type="submit"
                    className="modal-budget-icon-btn"
                    aria-label={updateCartMutation.isPending ? 'Enregistrement…' : t('save')}
                    title={updateCartMutation.isPending ? 'Enregistrement…' : t('save')}
                    aria-busy={updateCartMutation.isPending || undefined}
                    disabled={updateCartMutation.isPending}
                  >
                    {updateCartMutation.isPending ? (
                      <span className="modal-budget-spinner" aria-hidden="true" />
                    ) : (
                      <BudgetSaveGlyph />
                    )}
                  </LiquidGlassButton>
              </div>
            </form>
          </div>
        </div>
      )}

      <SelectionDock
        isOpen={selectionMode && selectedItems.size > 0}
        selectedCount={selectedItems.size}
        onOpenLinks={handleDockOpenLinks}
        onCopy={handleDockCopy}
        onMoveToSaved={handleDockMoveToSaved}
        onDelete={handleDockDelete}
        onClose={exitSelectionMode}
      />

      <LibraryPanel
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        anchorRef={cardsAnchorRef}
        onPlacementChange={handleLibraryPlacement}
        items={savedItems}
        isLoading={isLoadingLibrary && !Array.isArray(savedItemsData)}
        isError={isLibraryError && !Array.isArray(savedItemsData)}
        errorMessage={libraryError?.response?.data?.detail || libraryError?.message || ''}
        importSuccessItem={importSuccessItem}
        onAddToCart={(savedId) => moveFromLibraryMutation.mutate(savedId)}
        onDelete={(savedId) => {
          notify.confirm(t('deleteFromLibrary')).then((confirmed) => {
            if (confirmed) deleteFromLibraryMutation.mutate(savedId)
          })
        }}
        onImageError={handleItemImageError}
        adding={moveFromLibraryMutation.isPending}
        deleting={deleteFromLibraryMutation.isPending}
      />

      <CompareCartPicker
        isOpen={showComparePicker}
        onClose={() => setShowComparePicker(false)}
        sourceCart={currentCart}
        carts={carts}
        returnFocusRef={toolsButtonRef}
        onSelect={(cart) => {
          if (cart?.id) navigate(`/compare?cart1=${cartId}&cart2=${cart.id}`)
        }}
      />

      {showImageModal && selectedImage ? (
        <ProductImageLightbox
          slides={Array.isArray(selectedImage) ? selectedImage : [{ src: selectedImage, label: 'Image du produit' }]}
          onClose={() => { setShowImageModal(false); setSelectedImage(null) }}
        />
      ) : null}
      
      {/* Menu contextuel pour sélectionner la méthode d'actualisation */}
      <RefreshMethodContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        onSelectMethod={(method) => {
          setRefreshMethod(method)
          console.log('[CartDetailPage] Méthode d\'actualisation changée:', method)
        }}
        currentMethod={refreshMethod}
        skipWhitelist={skipWhitelist}
        onToggleWhitelist={(enabled) => {
          setSkipWhitelist(enabled)
          console.log('[CartDetailPage] Whitelist désactivée:', enabled)
        }}
      />
      
      {/* Modal d'optimisation budgétaire */}
      <BudgetOptimizationModal
        isOpen={showOptimizationModal}
        onClose={() => setShowOptimizationModal(false)}
        cartId={cartId}
        currentBudget={currentCart?.budget}
        currentTotal={total}
        currentItems={items || []}
        onOptimizationApplied={() => {
          // Rafraîchir les données du panier
          queryClient.invalidateQueries(['cart-items', cartId])
          queryClient.invalidateQueries(['carts'])
        }}
      />

      {/* Modal de partage */}
      <ShareCartModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        cartId={cartId}
        cartName={currentCart?.name}
      />

      {/* Dock de navigation */}
      <AppDock 
        onAppSelect={(appId) => {
          if (appId === 'house') {
            navigate('/carts', { state: { activeApp: 'house' } })
          } else if (appId === 'carts') {
            navigate('/carts', { state: { activeApp: 'carts' } })
          } else if (appId === 'analytics') {
            navigate('/carts', { state: { activeApp: 'analytics' } })
          } else if (appId === 'social') {
            navigate('/carts', { state: { activeApp: 'social' } })
          } else if (appId === 'settings') {
            navigate('/carts', { state: { activeApp: 'settings' } })
          }
        }} 
        selectedApp="carts"
      />
    </div>
  )
}

export default CartDetailPage





